/**
 * TownScene — the cozy SOCIAL HEARTH (CLAUDE.md v1 MVP step 3 + signature mechanics
 * #1–3; the "Barliman's chatroom" warmth). The first thing a tired player sees each
 * evening is a snug indoor TAVERN/INN — a warm wooden-plank floor, plastered walls
 * with a dusk window or two, a glowing hearth, hanging lanterns pooling amber light,
 * tables with stools, a bar counter and rugs — visibly *inhabited* by the simulated
 * society (resident trainers + their follower Pokémon milling about and lingering by
 * the tables), with the player and their lead Pokémon walking among them. A living
 * chat panel scrolls warm, world-grounded banter and greets the player by name;
 * quick-replies + a text box let them dip in (or ignore it entirely). And when a
 * resident who is present speaks, a soft overhead SPEECH BUBBLE pops over their head
 * (in addition to the chat line) so the room reads as alive and chatty. A notice-board
 * of fixtures opens the other scenes; the door ("Set out") heads to the region map.
 *
 * Layered cleanly: the renderer owns NO rules. Every fact comes from the live
 * GameSession (society, chat-director/voice, welcome-back letters). The environment
 * is drawn programmatically (soft Graphics shapes) so it is swappable for a real
 * tileset/interior later without touching this logic.
 *
 * Touch-first + responsive throughout: tap the floor to walk there, tap a fixture to
 * enter, tap a resident to greet; the chat panel reflows from a bottom sheet on a
 * phone to a side rail on a wide screen. Nothing is hover- or keyboard-only.
 */
import Phaser from 'phaser';
import type { GameSession, WelcomeBack } from '@/session';
import type { SimTrainer, VoiceLine, ChatChannel, PokemonInstance, Emotion } from '@/shared';
import type { DataIndex } from '@/data';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET, isCoarsePointer } from './ui/theme.js';
import { DPad } from './ui/DPad.js';
import { FocusRing } from './ui/FocusRing.js';
import { moveGrid, type GridDir } from './ui/gridNav.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { DialogBox } from './ui/DialogBox.js';
import { Menu, type MenuItem } from './ui/Menu.js';
import { ScrollList } from './ui/ScrollList.js';
import { TextInput } from './ui/TextInput.js';
import { SpeechBubble } from './ui/SpeechBubble.js';
import { canShowOff, showOffTarget, showOffEchoLine } from './chat/townChat.js';
import { Letter, Toast } from './ui/Toast.js';
import { Layout } from './layout/index.js';
import { SpriteResolver } from './sprites/index.js';
import {
  makePlayerTrainer,
  trainerKeyForSimTrainer,
  type TrainerAvatar,
} from './sprites/trainerAvatar.js';
import {
  createOverworldActor,
  fitOverworldActor,
  type OverworldActor,
} from './sprites/overworldActor.js';
import { followerScale, FOLLOWER_SCALE_DEFAULT } from '@/core';
import type { Direction } from './sprites/paths.js';
import { trainersManifestUrl } from './sprites/paths.js';
import {
  cardinalToDirection,
  DEFAULT_WANDER,
  initWander,
  pickLeadSpeciesId,
  stepFollow,
  stepWander,
  trailPointBehind,
  type Cardinal,
  type WanderBounds,
  type WanderState,
} from './overworld/wander.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import {
  areaDisplayName,
  exitsFor,
  OPPOSITE,
  spawnAtEdge,
  type AreaLink,
  type Edge,
} from './world/areaGraph.js';
import { isTopWorldTap } from './townTapGuard.js';
import { playMoveEffect } from './effects/moveEffects.js';
import { makeAnchoredActor, playShowdownAnim } from './effects/showdownFx.js';
import { getShowdownAnim } from './effects/showdownMoves.js';
import {
  actorDepthOrder,
  castableMoves,
  castReactReady,
  castTargetPoint,
  nudgeReticle,
  type CastableMove,
} from './townCast.js';
import { townWelcomeText, townWelcomeMaxWidth } from './topBarLayout.js';
import { questView, type QuestSurface } from './QuestLogScene.js';
import { rewardLine, isReadyToTurnIn } from './questLogFormat.js';
import { offerableQuests, type QuestWorldSlice } from '@/core/quests.js';
import type { QuestDef } from '@/shared';
import { REGISTRY_DATA_INDEX, REGISTRY_FX_READY, REGISTRY_SPRITES } from './PreloadScene.js';
import { getSession } from './session.js';

/** The town node id the society + chat-director key ambient life off of. */
const TOWN_NODE = 'town';
/** Channel the town chat reads (zone = the local hearth). */
const TOWN_CHANNEL: ChatChannel = 'zone';

/** How many detailed "regular" trainers we embody as full PMD sprites. */
const MAX_DETAILED = 5;
/**
 * How many ambient crowd sprites (pooled) mill about the tavern. The interior is a
 * SMALLER, more zoomed-in room and the sprites now render ~50% larger AGAIN (on top of
 * the earlier ~2×), so the far crowd is thinned a touch more (LOD) to keep the snug room
 * lively but clearly readable — a cozy tavern that's bustling, never an overcrowded
 * shuffle of big sprites jostling. Each crowd member also brings a follower, so the room
 * still reads densely social at this lower count.
 */
const MAX_CROWD = 3;

/**
 * Overworld sprite footprints (px, tallest side). These were already ~2× the original
 * cozy sizes; the owner then asked for "~50% MORE, still" on top of that — so each side
 * is bumped a further ~1.5× (e.g. labeled 150 → 224, crowd 134 → 200, player 160 → 240),
 * leaving the tavern's trainers + chibis large and clearly readable in the zoomed-in
 * interior (the tasteful-nod UX rule: "overworld sprites render larger"). The label and
 * speech-bubble hit-area / lift offsets are all DERIVED from these constants (see
 * spawnResident/spawnPlayer/positionBubble), so this single bump keeps everything in
 * proportion. The scale CAP is lifted further so the real (small) ripped art can actually
 * reach this size.
 */
const RESIDENT_LABELED_SIDE = 224;
const RESIDENT_CROWD_SIDE = 200;
const PLAYER_SIDE = 240;
const SPRITE_SCALE_CAP = 9;
/**
 * On a narrow/portrait PHONE the desktop footprints above are huge in the small view —
 * an overcrowded pile that overlaps the header + menu pills. So on a phone we shrink
 * every sprite footprint by this factor (and thin the crowd, below). A roomy desktop
 * keeps the full sizes. The hit-areas, name-label lifts, speech-bubble lifts, and the
 * follower's leader-relative sizing are all DERIVED from the LIVE footprint (the
 * `*Side` scene fields set in syncSpriteScale), so this single multiplier keeps the
 * whole tavern in proportion at either size.
 */
const PHONE_SPRITE_SCALE = 0.55;
/**
 * Phone LOD: thin the detailed cast + ambient crowd further on a small screen so the
 * snug room reads cleanly instead of as a jostling pile of (even shrunk) sprites.
 */
const PHONE_MAX_DETAILED = 3;
const PHONE_MAX_CROWD = 2;
/** Gap (px) between a sprite's head and its floating name label — scales with the bump. */
const NAME_LABEL_GAP = 8;

/**
 * Follower sizing reference (CLAUDE.md: followers are sized by Pokedex height,
 * hard-capped at 1.2× the trainer). `followerScale(height)` yields the desired
 * follower-to-LEADER footprint ratio (MIN..1.2); we render the follower's tallest
 * side at `leaderSide * ratio` — sized against ITS OWN leader (the player at
 * PLAYER_SIDE, each NPC at its resident side), never always the player's, so the
 * 1.2× cap holds for every leader. The follower chibi lives in a
 * `FOLLOWER_CONTAINER_SCALE`-zoomed container and is fitted (by its STABLE frame
 * size) to `FOLLOWER_FIT_BASE * cap`, so the cap passed to `attachActor` is
 * back-solved here.
 */
const FOLLOWER_CONTAINER_SCALE = 2.6;
const FOLLOWER_FIT_BASE = 40;
/**
 * Follower trailing tunables (mirrors the Route follower fix). FOLLOW_GAP is the small
 * arc-length a follower keeps behind its leader along the leader's breadcrumb trail;
 * FOLLOW_SPEED_FACTOR makes the follower's catch-up speed sit just ABOVE its leader's
 * walk speed so it can always close any lag a long walk opened (it is stepped EVERY
 * frame in onUpdate, so it keeps closing the gap even after the leader stops).
 */
const FOLLOW_GAP = 34;
const FOLLOW_SPEED_FACTOR = 1.15;
/**
 * The little BEHIND-AND-BELOW resting offset (px) a follower keeps from its leader so it
 * never STACKS on top of the trainer (the owner's "messy pile" screenshot). When a leader
 * stops, its breadcrumb trail collapses to a single point AT the leader, so the bare
 * trail-target would converge the follower right onto the trainer. We nudge the follow
 * target DOWN-screen by FOLLOW_BELOW so the partner rests just below its trainer's feet,
 * and the per-frame Y-sort (depthSortActors) then draws the trainer cleanly IN FRONT of
 * its own follower — a partner at heel, not a sprite-on-sprite smear.
 */
const FOLLOW_BELOW = 16;
/**
 * Per-walker render-depth band for the Y-SORT. Every overworld actor (player, follower,
 * NPC trainer, NPC follower) is RANK-ORDERED by its on-screen y each frame and assigned a
 * sequential depth from DEPTH_SORT_BASE upward, so whoever stands LOWER on screen (larger
 * y) draws IN FRONT — the classic overworld read where you can walk in front of / behind
 * another actor. Rank-ordering (rather than depth = raw y) is deliberate: raw screen-y runs
 * into the hundreds and would punch a sprite's depth up through the name labels (115),
 * bubbles (120) and modals (200+); the LOD-capped actor count is small, so sequential
 * ranks keep the whole sprite band comfortably between DEPTH_SORT_BASE and NAME_LABEL_DEPTH.
 * Ties break with a follower sorted just BEHIND its own leader, so a partner resting at the
 * trainer's feet never covers the trainer.
 */
const DEPTH_SORT_BASE = 10;
/** scaleCap such that `40*cap*containerScale === leaderSide * ratio`. */
function followerScaleCap(leaderSide: number, ratio: number): number {
  return (leaderSide * ratio) / (FOLLOWER_FIT_BASE * FOLLOWER_CONTAINER_SCALE);
}

/**
 * The cozy TAVERN INTERIOR palette (0xRRGGBB). A warm amber/brown set for the wooden-
 * plank floor, plaster walls, hearth, lanterns, and furniture — the snug-inn "social
 * hearth" read. Kept local to the scene (it's bespoke interior scenery, not part of the
 * shared dusk UI theme) and easy to retune or swap for a real interior tileset later.
 */
const TAVERN = {
  /** Warm plaster upper wall. */
  wall: 0x4a3a2c,
  /** A darker wainscot/shade line where wall meets floor. */
  wallShade: 0x2c2118,
  /** Wooden plank floor (warm brown). */
  floor: 0x6b4a30,
  /** Subtle seam line between planks. */
  plankSeam: 0x4a3320,
  /** Dark wood beams / window frames. */
  beam: 0x3a2818,
  /** Stone hearth surround. */
  stone: 0x5c5048,
  /** Glowing amber (hearth glow pool + lantern light). */
  glow: 0xf2b25a,
  /** Hot inner flame / lantern core. */
  flameHi: 0xffe08a,
  /** Outer flame. */
  flameLo: 0xf28b3c,
  /** A glowing ember log. */
  emberLog: 0x8a3a1e,
  /** Dusk sky in a window (deep blue night). */
  dusk: 0x2a3550,
  /** A warmer horizon glow at the window's base. */
  duskGlow: 0x6a5a78,
  /** A warm woven rug. */
  rug: 0x7a3a30,
  /** Rug trim ring. */
  rugTrim: 0xc99a5a,
  /** Round table base (darker wood). */
  tableBase: 0x4a3320,
  /** Round table top (lighter wood). */
  tableTop: 0x8a6038,
  /** A stool seat. */
  stool: 0x5a3e26,
  /** Bar counter base. */
  barBase: 0x4a3320,
  /** Bar counter top (lighter polished wood). */
  barTop: 0x9a6e40,
  /** A wooden barrel / crate body. */
  barrel: 0x6a4528,
  /** Barrel hoop / crate slat highlight. */
  barrelHoop: 0x3a2818,
  /** Crate face (a touch lighter than a barrel). */
  crate: 0x7a542e,
  /** A wall shelf plank. */
  shelf: 0x3f2c1c,
  /** A green glass bottle on a shelf. */
  bottleGreen: 0x3c6b3a,
  /** An amber glass bottle on a shelf. */
  bottleAmber: 0xb5762e,
  /** A pewter mug / plate sheen. */
  pewter: 0xb9b3a4,
  /** A framed wall painting's canvas (a soft warm landscape). */
  paintingSky: 0x7a6a52,
  /** A wall banner / pennant cloth (deep guild red). */
  banner: 0x8a3530,
  /** Banner trim (gold). */
  bannerTrim: 0xc99a5a,
  /** Cork notice-board backing. */
  board: 0x8a6a44,
  /** A pinned paper note on the board. */
  note: 0xe8dcc0,
  /** A potted-plant leaf (warm-lit green). */
  leaf: 0x4f6e3a,
  /** A clay plant pot. */
  pot: 0x6a4028,
  /** A hanging dried-herb bundle. */
  herb: 0x6f7a3e,
  /** A wax candle body. */
  candle: 0xe8dcc0,
} as const;

/**
 * Floating name-label anti-overlap thresholds (px, in world space). When two resident
 * name tags drift within this box of each other, the lower-priority one fades so they
 * never graze into one smudged line. The player's own tag always stays clear.
 */
const NAME_COLLIDE_X = 70;
const NAME_COLLIDE_Y = 18;
const NAME_DIM_ALPHA = 0.15;
/**
 * Name tags render at SCENE level (not nested in their walker's container) at this depth,
 * so a neighbour's now-larger sprite (default depth 0) can never draw over a label —
 * which was the "labels sometimes don't show" bug. Kept below the speech bubbles (120)
 * and the greeting / welcome-back overlays (200+), so a modal still reads cleanly.
 */
const NAME_LABEL_DEPTH = 115;

/**
 * Overhead speech bubbles: keep only a few on screen at once (nearby regulars + the
 * player) so the tavern reads cozy + chatty, never spammy. New bubbles past the cap
 * retire the oldest. Each bubble dwells ~5s then fades (see SpeechBubble).
 */
const MAX_BUBBLES = 3;
/** How far above a walker's feet-position the bubble's tail sits (px). */
const BUBBLE_LIFT = 8;

/**
 * Depth band for the hideable bottom MENU DRAWER. It must sit ABOVE the chat
 * bottom-sheet + the town sprites/labels/bubbles (so the nav buttons are always
 * tappable when open and never fight the chat), but BELOW the classic greeting /
 * welcome-back modals (200+), which still take precedence. The always-visible toggle
 * is drawn ABOVE the scrim AND the drawer panel so a tap on it always reaches the
 * toggle (closing the open drawer) — the full-viewport scrim used to sit over the
 * toggle and swallow that "tap again to close" tap, leaving the menu stuck open.
 */
const MENU_SCRIM_DEPTH = 160;
const MENU_DRAWER_DEPTH = 161;
const MENU_TOGGLE_DEPTH = 165;
/** The drawer's gentle slide/fade in/out (ms); instant under reduced motion. */
const MENU_SLIDE_MS = 220;
/**
 * The compact menu TOGGLE's side (px). A small square ICON chip (≡ / ×) kept at the
 * touch-target minimum so it stays fully tappable while reading as a quiet chip rather
 * than the old wide labelled pill the owner found too big + jarring.
 */
const TOGGLE_SIDE = TOUCH_TARGET;

/**
 * The top-bar welcome greeting draws ABOVE the room (sprites/labels/bubbles top out ~120,
 * the menu drawer at 160–165) so it stays readable over the larger overworld sprites — but
 * BELOW the modal band (200+). It used to sit at 210, ABOVE the modals, so the bright
 * "Welcome back, NAME." banner bled over the welcome-back letters modal + the greet dialog
 * in portrait. The modal scrim now dims the banner and the modal content draws cleanly on
 * top, so nothing overlaps the greeting line.
 */
const GREETING_DEPTH = 190;
/** Modal band (above the greeting): the welcome-back letters + the trainer-greet dialog. */
const MODAL_DEPTH = 200;

/**
 * The walkable EXIT SEAM at Town's BOTTOM edge → Sunny Meadow (the connected walkable
 * overworld; notes/r3-design/overworld.md). A soft trigger band across the lower-CENTRE
 * of the tavern floor, marked by a cream GB signpost ("↓ Sunny Meadow", styled to match
 * the DialogBox chrome). Walking into the band (or tapping the signpost, for phones) walks
 * you out to the meadow; the region map ("Set out") stays the instant fast-travel. The
 * band is kept CENTRED + clear of the bottom-left menu toggle and the bottom-right d-pad
 * so the seam never fights the UI. The meadow spawn uses the SAME world size RouteScene
 * does (WORLD_W/WORLD_H), so arrival lines up with the route's own edge spawns.
 */
const ROUTE_WORLD_W = 1280;
const ROUTE_WORLD_H = 900;
/** Height (px) of the bottom trigger band (measured up from the ground rect's bottom). */
const SEAM_BAND_H = 84;
/** The signpost draws ABOVE the room sprites but BELOW the name labels (115) + the UI. */
const SEAM_SIGN_DEPTH = 108;

/**
 * TAVERN MOVE CASTING — pure fun, no stakes. Tapping the follower (or pressing C)
 * opens a ▶-cursor menu of the lead/follower Pokémon's moves; choosing one plays that
 * move's procedural VFX FROM the follower's feet INTO thin air in front of it, in the
 * move's own type + category style. Repeatable, never a battle. The effect renders at
 * this depth (just below the chat/greet modals at 200) so a beam arcs over the room but
 * a conversation still reads on top; the move menu reuses the greet overlay's depth band.
 */
const CAST_EFFECT_DEPTH = 130;
/** How far (px) in front of the follower the thin-air cast point sits (clamped to floor). */
const CAST_REACH = 220;
/** How far (px) above the follower's feet the cast point lifts (so beams arc up-and-out). */
const CAST_LIFT = 130;
/** A calm casting pace for the shared effect engine (1 = the normal battle tempo). */
const CAST_PACE = 1;
/**
 * How far (px) above the follower's container feet the CASTER body anchor sits for the
 * Showdown cast path, so a beam emanates from the chibi's body rather than the floor
 * (mirrors BattleScene.effectAnchor's body lift). The procedural fallback keeps the raw
 * feet origin it always used.
 */
const CAST_CASTER_LIFT = 28;
/**
 * Minimum gap (ms) between NPC REACTIONS to a tavern cast. The cast animation fires on
 * every tap (instant + responsive), but free-aim lets the player tap-cast rapidly, so the
 * async, token-costing reaction is throttled to at most once per this window — delightful,
 * affordable (budget), and never spammy. While the cooldown is active a cast just plays its
 * VFX with no new reaction. Gated via the pure castReactReady helper; reset on re-entry.
 */
const CAST_REACT_COOLDOWN_MS = 7500;
/**
 * FREE-AIM cast mode chrome. After a move is picked, a light "cast mode" lets the player
 * TAP ANYWHERE to set the target and fire the move from the follower toward it — staying in
 * the mode so they can keep tapping new spots. The aim layer + reticle + prompt + Done chip
 * sit in this depth band: above the room sprites/labels/bubbles so the controls are always
 * reachable, but BELOW the modal band (200+). The reticle stays well clear of the live VFX
 * (CAST_EFFECT_DEPTH 130) and the prompt/Done sit above it so the affordances read clearly.
 */
const AIM_LAYER_DEPTH = 132;
const AIM_RETICLE_DEPTH = 134;
const AIM_PROMPT_DEPTH = 136;
/** Keyboard arrow nudge for the reticle (px per press) in cast mode. */
const AIM_NUDGE_STEP = 24;
/** The reticle's outer ring radius (px) — a clear, thumbable aim marker. */
const AIM_RETICLE_R = 16;

/**
 * "Walk over to talk" (the cozy two-way read): when the player addresses the room
 * (a typed line / quick-reply / show-off) or a present resident starts a line, one or
 * two NEARBY residents amble over to the player, stop at talking distance, face them,
 * and linger a beat while the exchange + bubbles play — then drift back to wandering.
 * Kept to a small cap so it's a warm "someone came to say hi", never a mob closing in.
 */
const MAX_APPROACHERS = 2;
/** Stop this far (px, feet-to-feet) from the player — close enough to chat, not crowding. */
const APPROACH_DISTANCE = 90;
/** How long an approacher lingers facing the player before resuming its wander (ms). */
const APPROACH_DWELL_MS = 4200;
/** Steady walk speed (px/sec) for an approaching resident — an unhurried amble over. */
const APPROACH_SPEED = 66;
/** Only residents already within this radius of the player are nominated to walk over. */
const APPROACH_NEARBY_RADIUS = 420;
/**
 * Minimum angular gap (radians) between two approachers' standoff slots around the player.
 * At APPROACH_DISTANCE the chord between two slots this far apart is wide enough that even
 * the larger desktop sprites don't overlap — so reacting residents ring AROUND the player,
 * never stacking onto the same spot (the tight phone pile the owner screenshotted).
 */
const APPROACH_SLOT_SEP = Math.PI / 3; // 60°
/** How close (px) an approacher must get to its assigned standoff point to count as arrived. */
const APPROACH_ARRIVE_EPS = 14;

/**
 * Tavern FIXTURES (a notice-board row of pills along the bottom of the floor) -> the
 * scenes/overlays they open. Framed as cozy inn furnishings; functionally the hub menu.
 */
interface Building {
  readonly key: string;
  readonly label: string;
  /** Contract scene key to navigate to (full-screen). */
  readonly scene: string;
}
const BUILDINGS: readonly Building[] = [
  { key: 'team', label: 'Team & Boxes', scene: 'TeamBoxes' },
  { key: 'dex', label: 'Living Dex', scene: 'Dex' },
  { key: 'quests', label: 'Quest Log', scene: 'Quests' },
  { key: 'training', label: 'Training Grounds', scene: 'Training' },
  { key: 'guild', label: 'Guild Hall', scene: 'Guild' },
  { key: 'codex', label: 'Lore Codex', scene: 'Codex' },
  { key: 'settings', label: 'Settings', scene: 'Settings' },
];

/** A wandering town inhabitant (player, regular, crowd member, or a follower). */
interface Walker {
  readonly trainer: SimTrainer | null; // null => the player or a follower
  readonly container: Phaser.GameObjects.Container;
  /** The real TRAINER avatar (the player AND every NPC resident). */
  avatar?: TrainerAvatar;
  /** A directional PMD chibi (a lead-Pokemon FOLLOWER, player or NPC). */
  actor?: OverworldActor;
  readonly label?: Phaser.GameObjects.Text;
  /** A soft "✦" overhead marker when this resident has an offerable / turn-in quest. */
  questMarker?: Phaser.GameObjects.Text;
  /**
   * Deliberate cardinal-wander state (NPC residents only). The player walks to tapped
   * destinations + keys; followers trail their leader — neither auto-wanders.
   */
  wander?: WanderState;
  /** The leader this walker trails (followers only). */
  leader?: Walker;
  /**
   * This walker's own trainer-sprite footprint (tallest side, px): PLAYER_SIDE for the
   * player, RESIDENT_LABELED_SIDE / RESIDENT_CROWD_SIDE for residents. A follower is
   * sized + capped against ITS OWN leader's footprint (not always the player's), so the
   * 1.2× cap holds for every leader regardless of how big that leader renders.
   */
  footprintSide?: number;
  /** Gap (px) a follower keeps behind its leader. */
  followGap?: number;
  /**
   * The Pokedex height of a follower's species (followers only), kept so a relayout can
   * re-fit the chibi against its leader's NEW footprint (sized by height, 1.2× capped).
   */
  dexHeight?: number;
  /** Steady speed (px/sec) for the player + followers (residents use the wander cfg). */
  speed: number;
  /**
   * "Walking over to talk to the player" state (residents only). While set, the
   * resident heads cardinally toward the player instead of wandering; on arrival it
   * faces them and parks until `approachUntil`, then clears + resumes its wander.
   */
  approaching?: boolean;
  /** Wall-clock ms after which an arrived approacher stops lingering (resumes wander). */
  approachUntil?: number;
  /** True once an approacher has reached its standoff point (so it parks + faces). */
  approachArrived?: boolean;
  /**
   * The per-approacher STANDOFF point (feet coords) this resident walks to — a distinct
   * slot on a ring AROUND the player, so several reacting residents gather in an arc
   * beside the player rather than piling ON the player/reticle (the tight phone cluster).
   * Assigned at nomination, cleared on release.
   */
  approachX?: number;
  approachY?: number;
  /** Last facing applied, so we only restart anims when the direction changes. */
  facing: Direction;
  /** True while moving so we can settle to idle when stopped. */
  moving: boolean;
  // Player-only: the tapped/keyed destination it strolls toward.
  targetX: number;
  targetY: number;
}

export class TownScene extends Phaser.Scene {
  private session!: GameSession;
  private layout!: Layout;
  private index?: DataIndex;
  private resolver!: SpriteResolver;
  private reduced = false;
  /**
   * Whether the vendored Showdown fx textures loaded in Preload (mirrors BattleScene's
   * `fxReady`). Gates the bespoke Showdown cast path; when false (fx pack absent) the
   * tavern cast falls back to the procedural {@link playMoveEffect}. Read once in create.
   */
  private fxReady = false;

  /**
   * The LIVE overworld sprite footprints (tallest side, px) for the current viewport:
   * the full desktop sizes on a roomy screen, multiplied by PHONE_SPRITE_SCALE on a
   * narrow/portrait phone. syncSpriteScale() recomputes these on every relayout and
   * re-fits the already-spawned avatars + hit-areas, so a rotation resizes everything.
   */
  private residentLabeledSide = RESIDENT_LABELED_SIDE;
  private residentCrowdSide = RESIDENT_CROWD_SIDE;
  private playerSide = PLAYER_SIDE;

  // The town "ground" the player + trainers wander on (left/main region).
  private groundRect = { x: 0, y: 0, width: 0, height: 0 };
  private ground?: Phaser.GameObjects.Graphics;

  private walkers: Walker[] = [];
  private player?: Walker;
  private follower?: Walker; // the lead Pokémon trailing the player
  /** Trainer ids who currently have an offerable OR turn-in-ready quest (the "✦" cast). */
  private questGiverIds = new Set<string>();
  /**
   * Live overhead speech bubbles, each pinned to the walker who spoke so it floats over
   * their head as they mill about. Capped (MAX_BUBBLES) + auto-retiring so the room stays
   * cozy. A bubble removes itself from this list when it fades out (onRetire).
   */
  private bubbles: { walker: Walker; bubble: SpeechBubble }[] = [];
  /** Followers (player + every NPC) keep a short breadcrumb trail of their leader. */
  private trails = new Map<Walker, { x: number; y: number }[]>();
  /** Residents currently walking over to talk to the player (cozy cap MAX_APPROACHERS). */
  private approachers = new Set<Walker>();
  /** Available trainer-sprite keys (manifest) for deterministic NPC assignment. */
  private trainerKeys: readonly string[] = [];

  // Keyboard movement (arrow keys + WASD), folded with the tap-to-walk destination.
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
  /**
   * The on-screen d-pad (shared component) so the tavern is walkable by touch on a phone —
   * folded into keyboardVector() alongside the arrows/WASD. Shown only on a coarse-pointer
   * (touch) device; a mouse desktop walks by keyboard and hides the pad to stay clean.
   */
  private dpad?: DPad;
  /**
   * Keyboard nav over the open nav-drawer buttons (mouse-free desktop): arrows move a
   * highlight across the grid, Enter enters that building. Live only while the drawer is
   * open; the ring is hidden otherwise.
   */
  private menuNavIndex = -1;
  private menuNavCols = 1;
  private menuFocusRing?: FocusRing;
  private menuNavKeyHandler?: (e: KeyboardEvent) => void;

  // Chat hearth.
  private chatPanel?: Panel;
  private chatList?: ScrollList;
  private chatTitle?: Phaser.GameObjects.Text;
  private chatInput?: TextInput;
  private quickReplies: Button[] = [];
  private chatCursorY = 0;
  private chatLineWidth = 0;
  private chatStreaming = false;
  private chatTimer?: Phaser.Time.TimerEvent;

  // Top bar + actions.
  private greeting?: Phaser.GameObjects.Text;
  private menuButtons: { building: Building; button: Button }[] = [];
  private setOutButton?: Button;
  private busy = false;

  // ── the walkable EXIT SEAM (Town → Sunny Meadow) ─────────────────────────────
  /** The cream GB signpost at Town's bottom edge (container: post + board + label). */
  private seamSign?: Phaser.GameObjects.Container;
  /** Town's authored exit link (Town → meadow), derived from the area graph. */
  private seamLink?: AreaLink;
  /**
   * Debounce: false right after arriving from a route (the player spawns INSIDE the band)
   * until they leave the band, so the seam never re-triggers on arrival. Re-arms on exit.
   */
  private seamArmed = true;
  /** True once a seam transition has fired, so it can never double-navigate. */
  private seamTriggering = false;
  /**
   * The town edge to seat the player at when arriving from a route (set from create()'s
   * `entryEdge`). Only 'down' is used today (walking up out of the meadow → Town's bottom).
   */
  private arrivalEdge?: Edge;

  // ── the hideable bottom MENU DRAWER ──────────────────────────────────────────
  // The nav buttons used to sit as pills directly over the cozy tavern + its sprites
  // (the owner's screenshot: clutter on a small phone view). They now live in a
  // collapsible bottom drawer behind a small always-visible toggle, so the hidden
  // state is a CLEAN scene — just the room, the chat, the toggle, and "Set out".
  /** The always-visible compact "≡ / ×" icon-chip toggle that opens/closes the drawer. */
  private menuToggle?: Button;
  /** The slide-up drawer panel that holds the nav buttons (+ a heading). */
  private menuDrawer?: Panel;
  /** The drawer's heading label. */
  private menuHeading?: Phaser.GameObjects.Text;
  /** A dim scrim behind the open drawer; tapping it (outside) closes the drawer. */
  private menuScrim?: Phaser.GameObjects.Rectangle;
  /** Whether the drawer is currently shown. Default: open on desktop, closed on phone. */
  private menuOpen = false;
  /** The live drawer geometry (recomputed each relayout) so animation + taps agree. */
  private drawerRect = { x: 0, y: 0, width: 0, height: 0 };
  /** A live slide/fade tween so a rapid re-toggle doesn't stack tweens. */
  private menuTween?: Phaser.Tweens.Tween;

  // The classic greeting overlay (a cream DialogBox + a ▶-cursor choice Menu).
  private greetDialog?: DialogBox;
  private greetMenu?: Menu;
  private greetScrim?: Phaser.GameObjects.Rectangle;

  // ── Tavern move casting (tap the follower / press C → cast a move into thin air) ──
  /** The ▶-cursor menu of the follower's castable moves. */
  private castMenu?: Menu;
  /** A soft scrim behind the cast menu (tapping it cancels). */
  private castScrim?: Phaser.GameObjects.Rectangle;
  /** A small "✦ Cast" prompt label that floats over the follower so it's discoverable. */
  private castHint?: Phaser.GameObjects.Text;
  /** True while a cast effect is mid-flight, so we don't stack effects on one mon. */
  private casting = false;
  /**
   * Scene-clock ms of the last NPC reaction to a tavern cast, or null if none yet. The cast
   * animation fires every tap; this throttles the (async) NPC reaction to once per
   * CAST_REACT_COOLDOWN_MS via castReactReady. Reset on re-entry (resetState).
   */
  private castReactAt: number | null = null;

  // ── FREE-AIM cast mode (tap anywhere to aim; re-castable until Done/Esc) ──────────
  /** The move currently being aimed in cast mode (null when not aiming). */
  private aimMove?: CastableMove;
  /** A full-room transparent layer that captures the NEXT tap anywhere as the aim point. */
  private aimLayer?: Phaser.GameObjects.Rectangle;
  /** The drawn reticle marking where the move will fire (the last tapped / keyed point). */
  private aimReticle?: Phaser.GameObjects.Graphics;
  /** The live reticle position (world space); seeded in front of the follower. */
  private aimPoint = { x: 0, y: 0 };
  /** The "Tap anywhere to cast <Move>" hint banner shown while aiming. */
  private aimPrompt?: Phaser.GameObjects.Text;
  /** The "✕ Done" affordance that exits cast mode (touch/mouse parity for Esc). */
  private aimDone?: Button;
  /** A soft idle pulse on the reticle (stopped under reduced motion / on teardown). */
  private aimReticleTween?: Phaser.Tweens.Tween;
  /** Enter (fire at reticle) + Esc (exit) keys, live only while aiming. */
  private aimEnterKey?: Phaser.Input.Keyboard.Key;
  private aimEscKey?: Phaser.Input.Keyboard.Key;

  constructor() {
    super('Town');
  }

  create(data?: {
    welcomeBack?: WelcomeBack;
    followerCared?: Emotion;
    /** Set by a RouteScene seam: the player walked UP out of the meadow into Town. */
    fromRoute?: boolean;
    /** Which Town edge to seat the player at when arriving from a route (today: 'down'). */
    entryEdge?: Edge;
  }): void {
    // ── CRITICAL: Phaser REUSES this scene instance, so create() runs again on every
    // RE-ENTRY (Town → Codex → back). Instance FIELDS (the button/walker/quick-reply
    // arrays, the busy/streaming flags, the chat cursor) survive between visits, so we
    // must reset them to empty here — otherwise buildMenu() etc. PUSH a second set onto
    // the stale one and the menu renders doubled, shifted, and dead. The previous
    // visit's display objects were destroyed by SHUTDOWN; here we drop the references.
    this.resetState();

    // A warm dark backdrop behind the snug interior (the tavern's dim, cozy gloom).
    this.cameras.main.setBackgroundColor(0x140f0a);
    this.session = getSession(this);
    this.layout = Layout.attach(this);
    this.index = this.registry.get(REGISTRY_DATA_INDEX) as DataIndex | undefined;
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolver(this);
    this.reduced = isReducedMotion();
    this.fxReady = this.registry.get(REGISTRY_FX_READY) === true;

    this.ground = this.add.graphics();

    // The walkable exit seam (Town → meadow). Derive the authored link from the area graph
    // and, if we arrived from a route (walked up out of the meadow), remember which town
    // edge to seat the player at so spawnPlayer places them by the signpost, not at random.
    this.seamLink = exitsFor('Town')[0];
    if (data?.fromRoute) this.arrivalEdge = data.entryEdge ?? 'down';

    this.buildTopBar();
    this.buildMenu();
    this.buildChatPanel();
    this.buildSetOut();
    this.buildSeam();
    this.setupKeyboard();

    this.relayout();
    this.layout.on('change', () => this.relayout());

    // DEFAULT drawer state: collapsed on a phone (a clean tavern view — the owner's
    // request), open on a roomy desktop where there's space to show the nav buttons.
    // Set after the first relayout so the drawer geometry is known. Reduced motion
    // skips the slide; layoutMenu has already placed the closed state on a phone.
    if (!this.phoneMode) this.setMenuOpen(true);

    // Populate the world + start the chat pump after layout has sized the ground.
    void this.populateTown();
    void this.startChat();

    // A warm welcome-back, if the entry flow (or a resume) handed us one.
    if (data?.welcomeBack && data.welcomeBack.letters.length > 0) {
      this.showWelcomeBack(data.welcomeBack);
    }

    // Returning from the Care room: once the follower has trailed into place, pop a brief
    // warm reaction (hearts + a hop) over it. Poll a few times since populateTown spawns the
    // follower asynchronously.
    if (data?.followerCared) {
      const emotion = data.followerCared;
      let tries = 0;
      const tryReact = (): void => {
        if (!this.scene.isActive()) return;
        if (this.follower?.container.active) {
          this.reactFollowerMood(emotion);
        } else if (tries++ < 6) {
          this.time.delayedCall(200, tryReact);
        }
      };
      this.time.delayedCall(this.reduced ? 100 : 400, tryReact);
    }

    this.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    // Save on tab close so closing mid-pottering always feels okay (no data lost).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => void this.session.save());

    fadeIn(this);
  }

  /**
   * Drop every reference that a prior visit's create() populated, so re-entry starts
   * from a clean slate. Display objects themselves were torn down by the previous
   * SHUTDOWN (teardown()); this just clears the arrays/flags so nothing stacks. Safe to
   * call on a first entry too (everything is already empty).
   */
  private resetState(): void {
    this.walkers = [];
    this.player = undefined;
    this.follower = undefined;
    this.trails.clear();
    this.approachers.clear();
    this.bubbles = [];
    this.menuButtons = [];
    this.quickReplies = [];
    this.chatPanel = undefined;
    this.chatList = undefined;
    this.chatTitle = undefined;
    this.chatInput = undefined;
    this.greeting = undefined;
    this.setOutButton = undefined;
    this.seamSign = undefined;
    this.seamLink = undefined;
    this.seamArmed = true;
    this.seamTriggering = false;
    this.arrivalEdge = undefined;
    this.menuToggle = undefined;
    this.menuDrawer = undefined;
    this.menuHeading = undefined;
    this.menuScrim = undefined;
    this.menuTween = undefined;
    this.menuOpen = false;
    this.drawerRect = { x: 0, y: 0, width: 0, height: 0 };
    this.ground = undefined;
    this.greetDialog = undefined;
    this.greetMenu = undefined;
    this.greetScrim = undefined;
    this.castMenu = undefined;
    this.castScrim = undefined;
    this.castHint = undefined;
    this.casting = false;
    this.castReactAt = null;
    this.aimMove = undefined;
    this.aimLayer = undefined;
    this.aimReticle = undefined;
    this.aimPoint = { x: 0, y: 0 };
    this.aimPrompt = undefined;
    this.aimDone = undefined;
    this.aimReticleTween = undefined;
    this.aimEnterKey = undefined;
    this.aimEscKey = undefined;
    this.dpad = undefined;
    this.menuFocusRing = undefined;
    this.menuNavKeyHandler = undefined;
    this.menuNavIndex = -1;
    this.menuNavCols = 1;
    this.chatTimer = undefined;
    this.chatCursorY = 0;
    this.chatLineWidth = 0;
    this.chatStreaming = false;
    this.busy = false;
  }

  /** Arrow keys + WASD drive the player in town (touch/tap still works alongside). */
  private setupKeyboard(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    this.cursors = kb.createCursorKeys();
    this.wasd = {
      up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
    // C casts a move (keyboard parity with tapping the follower). Registering the key
    // captures it (preventDefault) for consistency; the actual open runs off the keydown
    // event, which the chat TextInput's keyboard suppression silences while typing.
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.C);
    kb.on('keydown-C', this.onCastKey, this);
    // V opens the follower Care room (keyboard parity with tapping the follower → ♥ Care).
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.V);
    kb.on('keydown-V', this.onCareKey, this);
    // M toggles the nav drawer (keyboard parity with the ≡ chip); Esc closes it (existing).
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.M);
    kb.on('keydown-M', this.onMenuKey, this);

    // The shared on-screen d-pad: touch-first walking, folded into keyboardVector(). Built
    // here (after the keys) so relayout() can seat + show/hide it. Kept for coarse-pointer
    // (touch) devices; a mouse desktop walks by keyboard and hides the pad to stay clean.
    this.dpad = new DPad(this, { reducedMotion: this.reduced, depth: MENU_SCRIM_DEPTH - 1 });

    // Keyboard nav over the open nav drawer, so the desktop flow is fully mouse-free.
    this.menuFocusRing = new FocusRing(this, { depth: MENU_TOGGLE_DEPTH - 1 });
    this.menuNavKeyHandler = (e: KeyboardEvent): void => this.onMenuNavKey(e);
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.menuNavKeyHandler);
    }
  }

  /**
   * Arrow/Enter nav over the OPEN nav drawer's building buttons (mouse-free desktop). Only
   * acts while the drawer is open; suppressed while a DOM text field (the chat box) is
   * focused. Esc is handled separately (onEscapeMenu closes the drawer).
   */
  private onMenuNavKey(e: KeyboardEvent): void {
    if (!this.menuOpen) return;
    if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
    const n = this.menuButtons.length;
    if (n === 0) return;
    const dirs: Record<string, GridDir> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    };
    const dir = dirs[e.key];
    if (dir) {
      e.preventDefault();
      this.setMenuNav(
        this.menuNavIndex < 0 ? 0 : moveGrid(this.menuNavIndex, dir, n, this.menuNavCols),
      );
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (this.menuNavIndex >= 0 && this.menuButtons[this.menuNavIndex]) {
        e.preventDefault();
        this.enterBuilding(this.menuButtons[this.menuNavIndex]!.building);
      }
    }
  }

  /** Seat the keyboard highlight on an open-drawer button + draw the focus ring. */
  private setMenuNav(index: number): void {
    const n = this.menuButtons.length;
    if (n === 0) return;
    this.menuNavIndex = Math.max(0, Math.min(n - 1, index));
    const btn = this.menuButtons[this.menuNavIndex]?.button;
    if (!btn) return;
    this.menuFocusRing?.show(btn.x, btn.y, btn.buttonWidth, btn.buttonHeight);
    this.menuFocusRing?.bringToTop();
  }

  /**
   * M-key: toggle the nav drawer (keyboard parity with the ≡ toggle chip). Suppressed
   * while the chat TextInput is focused (Phaser's keyboard plugin is disabled then), so a
   * typed "m" never toggles the menu. toggleMenu itself guards busy.
   */
  private onMenuKey(): void {
    this.toggleMenu();
  }

  /**
   * C-key: open the cast menu (keyboard parity with tapping the follower). While the
   * chat TextInput is focused it fully suppresses Phaser's keyboard plugin (so a typed
   * "c" never reaches here), and openCastMenu itself guards busy / an open overlay — so
   * this stays a no-op whenever it shouldn't fire.
   */
  private onCastKey(): void {
    this.openCastMenu();
  }

  /** V-key: open the follower Care room (keyboard parity with the ♥ Care tap affordance). */
  private onCareKey(): void {
    if (this.busy || this.casting || this.aimMove || this.greetDialog || this.castMenu) return;
    this.openCareForFollower();
  }

  /** The current keyboard movement vector (arrows + WASD), or null if no key is held. */
  private keyboardVector(): { x: number; y: number } | null {
    // While the nav drawer is open, arrows drive the drawer highlight (not walking).
    if (this.menuOpen) return null;
    let x = 0;
    let y = 0;
    if (this.cursors?.left.isDown || this.wasd?.left.isDown) x -= 1;
    if (this.cursors?.right.isDown || this.wasd?.right.isDown) x += 1;
    if (this.cursors?.up.isDown || this.wasd?.up.isDown) y -= 1;
    if (this.cursors?.down.isDown || this.wasd?.down.isDown) y += 1;
    // Fold in the on-screen d-pad (touch) so a phone walks the tavern too.
    if (this.dpad?.isVisible) {
      x += this.dpad.vector.x;
      y += this.dpad.vector.y;
    }
    x = Phaser.Math.Clamp(x, -1, 1);
    y = Phaser.Math.Clamp(y, -1, 1);
    return x === 0 && y === 0 ? null : { x, y };
  }

  // ── depth-0 world-tap guard (the "stuck in Town" fix) ─────────────────────────

  /**
   * Is `target` the GENUINELY top-most interactive object under this pointer?
   *
   * The scene keeps Phaser's DEFAULT top-only hit-testing (`input.topOnly = TRUE`), so
   * Phaser's own dispatch already delivers a tap to just the single top-most interactive
   * object — a control over a walker should win on its own. This guard is BELT-AND-
   * SUSPENDERS on top of that: Town's trainer WALKERS and the walkable GROUND are
   * interactive at depth 0, beneath the menu drawer/toggle, "Set out", the chat panel/rows,
   * and any modal scrim; the guard makes doubly sure a depth-0 world handler only fires
   * when ITS object is genuinely on top — so a tap on one of those CONTROLS can never also
   * greet a trainer (busy=true + a greet overlay) or strand the player out of setting out /
   * opening the menu / entering a building, even if some future stacking or a stray
   * per-object hit slipped a lower object through.
   *
   * (ScrollList no longer flips the scene-wide `topOnly` flag — it tracks its touch-drag at
   * the scene input level instead — so nothing globally re-enables pass-through hit-testing.)
   * Each depth-0 world handler calls this: we ask Phaser's input plugin which interactive
   * objects are under the pointer (`hitTestPointer`), sort them TOP-FIRST with the SAME
   * `sortGameObjects` Phaser's own dispatch uses (highest render order — i.e. highest depth
   * — first), and only act when OUR object is the one on top.
   */
  private isTopHit(pointer: Phaser.Input.Pointer, target: Phaser.GameObjects.GameObject): boolean {
    const hits = this.input.hitTestPointer(pointer);
    // hitTestPointer returns UNSORTED objects; Phaser's own pointer dispatch sorts them
    // top-first before delivering, so we do the same to read the genuine top object.
    this.input.sortGameObjects(hits, pointer);
    return isTopWorldTap(hits, target);
  }

  // ── top bar (warm greeting) ──────────────────────────────────────────────────

  private buildTopBar(): void {
    // The text + font + wrap width are (re)applied for the live viewport in relayout()
    // via syncGreeting(): on a phone a COMPACT line at a smaller font, width-constrained
    // to stay clear of the top-right Set out button + the tavern crowd labels; on desktop
    // the full lively line at the large font. Seed with the desktop line so frame one is
    // sensible before the first relayout.
    this.greeting = this.add
      .text(0, 0, this.welcomeLine(false), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
        // A cream halo so the header stays readable over the larger overworld sprites
        // (they otherwise drew on top of the plain text). Depth sits above the room but
        // BELOW the modal band so a modal (welcome-back / greet) draws cleanly over it.
        stroke: CSS.bg,
        strokeThickness: 5,
      })
      .setDepth(GREETING_DEPTH);
  }

  /** Anyone home? (drives the warm "lively tonight" tail of the welcome line). */
  private get tavernLively(): boolean {
    return this.session.society.some((t) => t.homeNodeId === TOWN_NODE);
  }

  private welcomeLine(compact: boolean): string {
    return townWelcomeText(this.session.trainerName, this.tavernLively, compact);
  }

  /**
   * Apply the welcome line for the current viewport: a COMPACT, smaller-font, width-
   * constrained greeting on a phone (so it never overflows into the Set out button or the
   * crowd's name labels), the full lively line at the large font on a roomy desktop. The
   * width cap excludes the top-right Set out column, so the (wrapping) greeting keeps its
   * own clear band on the left.
   */
  private syncGreeting(compact: boolean): void {
    const g = this.greeting;
    if (!g) return;
    const safe = this.layout.safe;
    g.setText(this.welcomeLine(compact));
    g.setFontSize(compact ? FONT.size.sm : FONT.size.lg);
    if (compact) {
      const setOutW = this.setOutButton?.buttonWidth ?? 200;
      const maxW = townWelcomeMaxWidth({
        safeX: safe.x,
        safeWidth: safe.width,
        leftPad: SPACING.lg,
        rightPad: SPACING.lg,
        buttonWidth: setOutW,
        margin: SPACING.md,
        min: 120,
      });
      g.setWordWrapWidth(maxW, true);
    } else {
      // Desktop: no wrap; the single line is short and sits well clear of the corner.
      g.setWordWrapWidth(null, false);
    }
  }

  // ── building menu (a HIDEABLE bottom drawer that opens the other scenes) ───────

  /**
   * Build the menu DRAWER: a small always-visible toggle, a slide-up panel + heading,
   * a scrim, and the nav buttons that live INSIDE the panel. The buttons no longer
   * sit over the cozy room — they're tucked in the drawer so the hidden state is a
   * clean tavern view. Geometry + the open/closed transform are applied in relayout()
   * / setMenuOpen(); here we just create the objects and wire their inputs.
   */
  private buildMenu(): void {
    // The always-visible toggle. A COMPACT, quiet ghost ICON CHIP — just the classic ≡
    // glyph (× when open) at a touch-target square — so the hidden state stays clean and
    // the toggle reads as a small unobtrusive chip, not a wide labelled pill, while still
    // being a full tappable target. Width + height are fixed at TOGGLE_SIDE in
    // positionToggle / layoutMenu so it never re-inflates into a pill on relayout.
    this.menuToggle = new Button(this, 0, 0, {
      label: '≡',
      variant: 'ghost',
      fontSize: FONT.size.md,
      width: TOGGLE_SIDE,
      height: TOGGLE_SIDE,
      reducedMotion: this.reduced,
      onClick: () => this.toggleMenu(),
    });
    this.menuToggle.setDepth(MENU_TOGGLE_DEPTH);

    // The scrim sits behind the open drawer; a tap on it (i.e. outside the panel)
    // closes the drawer. Created hidden; sized + shown in setMenuOpen().
    this.menuScrim = this.add
      .rectangle(0, 0, 10, 10, COLORS.scrim, 0.45)
      .setOrigin(0.5)
      .setDepth(MENU_SCRIM_DEPTH)
      .setVisible(false);
    this.menuScrim.setInteractive();
    this.menuScrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.setMenuOpen(false));

    // The drawer panel + its heading. Created hidden; positioned in layoutMenu().
    this.menuDrawer = new Panel(this, 0, 0, { width: 320, height: 200, shadow: true });
    this.menuDrawer.setDepth(MENU_DRAWER_DEPTH).setVisible(false);
    this.menuHeading = this.add
      .text(0, 0, 'Where to?', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.accent,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(MENU_DRAWER_DEPTH + 1)
      .setVisible(false);

    for (const building of BUILDINGS) {
      const button = new Button(this, 0, 0, {
        label: building.label,
        variant: 'ghost',
        reducedMotion: this.reduced,
        fontSize: FONT.size.sm,
        height: 44,
        onClick: () => this.enterBuilding(building),
      });
      // Buttons live ABOVE the drawer panel so they're tappable when open; hidden with
      // the drawer so a closed drawer never leaves stray pills over the room.
      button.setDepth(MENU_DRAWER_DEPTH + 1).setVisible(false);
      this.menuButtons.push({ building, button });
    }

    // Esc closes an open drawer (keyboard parity with the scrim tap).
    this.input.keyboard?.on('keydown-ESC', this.onEscapeMenu, this);
  }

  /** Keyboard: Escape closes the drawer if it's open (never strands the player). */
  private onEscapeMenu(): void {
    if (this.menuOpen) this.setMenuOpen(false);
  }

  /** Flip the drawer open/closed (the toggle's action). */
  private toggleMenu(): void {
    this.setMenuOpen(!this.menuOpen);
  }

  /**
   * Show or hide the menu drawer with a gentle slide + fade (instant under reduced
   * motion). When OPEN: the scrim + panel + heading + nav buttons become visible and
   * the panel slides up from below its resting band into view; the toggle reads
   * "× Close". When CLOSED: it slides back down + fades out, then the objects hide so
   * the tavern view is clean again. Idempotent and tween-safe (a rapid re-toggle
   * cancels the prior tween).
   */
  private setMenuOpen(open: boolean): void {
    if (this.busy && open) return; // don't pop the drawer mid-navigation
    this.menuOpen = open;
    // The keyboard highlight starts unset each time the drawer opens (first arrow seats
    // it) and is cleared when it closes so a stray ring never lingers over the room.
    this.menuNavIndex = -1;
    this.menuFocusRing?.hide();
    // A quiet glyph chip: ≡ to open, × to close (no wide label). Width stays fixed at the
    // compact TOGGLE_SIDE so the chip never re-inflates into a pill.
    this.menuToggle?.setLabel(open ? '×' : '≡');
    // Re-seat the toggle for the new state (tab above the drawer when open, bottom-left
    // of the room when closed) so a tap on it always reaches the toggle — never the scrim.
    this.menuToggle?.setWidth(TOGGLE_SIDE);
    this.positionToggle();
    // Re-seat the exit signpost for the new drawer state (lift it clear when the drawer
    // opens over the bottom-centre; drop it back to the band when it closes).
    this.positionSeam();

    const drawer = this.menuDrawer;
    const heading = this.menuHeading;
    const scrim = this.menuScrim;
    if (!drawer || !scrim) return;

    this.menuTween?.remove();
    this.menuTween = undefined;

    const rect = this.drawerRect;
    const restX = rect.x + rect.width / 2; // the drawer's in-view resting centre (x)
    const restY = rect.y + rect.height / 2; // the drawer's in-view resting centre (y)
    const hiddenY = rect.y + rect.height + drawer.panelHeight; // tucked below the screen

    // Panel, Text + Button all share these mixins (visible/alpha/transform), so one
    // structurally-typed array drives both the reveal/hide and the slide/fade tween.
    // (Cast via the GameObject base — the public classes don't re-declare every mixin
    // field, but each instance carries them at runtime.)
    type DrawerItem = Phaser.GameObjects.GameObject & {
      y: number;
      setVisible(v: boolean): unknown;
      setAlpha(a: number): unknown;
    };
    const items: DrawerItem[] = [
      drawer as unknown as DrawerItem,
      ...(heading ? [heading as unknown as DrawerItem] : []),
      ...this.menuButtons.map((m) => m.button as unknown as DrawerItem),
    ];

    if (open) {
      // Reveal everything + place it at the resting band first, then (unless reduced)
      // nudge the whole drawer DOWN by `dy` and tween it back UP with a fade.
      scrim.setVisible(true);
      for (const it of items) it.setVisible(true);
      // ANCHOR the drawer Panel to its resting centre (BOTH x and y) every open. Without
      // this the panel kept its create-time (0,0) — or a stale prior position — because
      // the slide is a RELATIVE tween (+=dy / -=dy) that returns to wherever it started,
      // and only `y` was ever set (reduced path). That left an empty cream box floating
      // top-left after Close → re-open. Setting it here (then the heading + buttons, which
      // layoutMenuContents pins relative to the same rect) moves the whole drawer as one.
      drawer.setPosition(restX, restY);
      this.layoutMenuContents(); // heading + buttons relative to the resting drawer
      if (this.reduced) {
        drawer.setAlpha(1).setPosition(restX, restY);
        scrim.setAlpha(0.45);
        return;
      }
      const dy = hiddenY - restY;
      // Start offset below + transparent.
      for (const it of items) {
        it.y += dy;
        it.setAlpha(0);
      }
      scrim.setAlpha(0);
      this.tweens.add({
        targets: scrim,
        alpha: 0.45,
        duration: MENU_SLIDE_MS,
        ease: 'Sine.easeOut',
      });
      this.menuTween = this.tweens.add({
        targets: items,
        y: `-=${dy}`,
        alpha: 1,
        duration: MENU_SLIDE_MS,
        ease: 'Sine.easeOut',
        onComplete: () => {
          this.menuTween = undefined;
        },
      });
    } else {
      // Slide back down + fade, then hide so the room view is clean again.
      const finish = (): void => {
        for (const it of items) it.setVisible(false);
        scrim.setVisible(false);
        this.menuTween = undefined;
      };
      if (this.reduced) {
        finish();
        return;
      }
      const dy = hiddenY - restY;
      this.tweens.add({ targets: scrim, alpha: 0, duration: MENU_SLIDE_MS, ease: 'Sine.easeIn' });
      this.menuTween = this.tweens.add({
        targets: items,
        y: `+=${dy}`,
        alpha: 0,
        duration: MENU_SLIDE_MS,
        ease: 'Sine.easeIn',
        onComplete: finish,
      });
    }
  }

  private enterBuilding(building: Building): void {
    if (this.busy) return;
    // If the scene is somehow not active, don't latch busy (which would lock the menu);
    // just bail. The happy path sets busy + navigates synchronously, so goTo (now always
    // reliable) takes the scene away and the next entry's resetState clears busy.
    if (!this.scene.isActive()) return;
    this.busy = true;
    void this.session.save();
    goTo(this, building.scene);
  }

  // ── set out ───────────────────────────────────────────────────────────────────

  private buildSetOut(): void {
    this.setOutButton = new Button(this, 0, 0, {
      label: 'Set out ›',
      variant: 'primary',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => void this.setOut(),
    });
    // Above the depth-0 walkers so a walker wandering under it never wins the hit-test
    // (which would fire a greet instead of navigating — the "stuck in Town" bug).
    this.setOutButton.setDepth(MENU_TOGGLE_DEPTH);
  }

  private async setOut(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setOutButton?.setDisabled(true);
    let navigating = false;
    try {
      try {
        await this.session.setOut();
      } catch {
        // setOut only persists; a hiccup must never strand the player.
      }
      // If the scene was torn down while we awaited, bail WITHOUT navigating (the finally
      // below resets busy + re-enables the button so a later Set out is never locked out).
      if (!this.scene.isActive()) return;
      navigating = true;
      goTo(this, 'RegionMap');
    } finally {
      // Only reset the busy/disabled lifecycle when we did NOT navigate. A successful
      // goTo leaves busy=true on purpose (the scene is leaving); but ANY early/non-
      // navigating exit must clear busy + re-enable the button so Set out never stays
      // permanently locked (the "stuck in Town" bug).
      if (!navigating) {
        this.busy = false;
        this.setOutButton?.setDisabled(false);
      }
    }
  }

  // ── the walkable exit seam (Town → Sunny Meadow) ──────────────────────────────

  /**
   * Build the cream GB SIGNPOST that marks Town's bottom exit to the meadow: a small wooden
   * post under a double-bordered cream board (matching the DialogBox chrome) reading
   * "↓ Sunny Meadow". It's a big tappable target too (tap → walk out, for phones). Geometry
   * is seated each relayout by positionSeam(); here we just make the objects + wire the tap.
   */
  private buildSeam(): void {
    const dest = this.seamLink?.toArea ?? 'node-meadow';
    const container = this.add.container(0, 0).setDepth(SEAM_SIGN_DEPTH);
    const gfx = this.add.graphics();
    const label = this.add
      .text(0, 0, `↓ ${areaDisplayName(dest)}`, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxText,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // Board sized to the label; the post drops from the board down to the container origin
    // (the "feet" the sign plants at). Everything is drawn in container-local coords.
    const padX = SPACING.md;
    const padY = SPACING.xs;
    const w = label.width + padX * 2;
    const h = label.height + padY * 2;
    const postH = 22;
    const boardCy = -postH - h / 2;

    // Wooden post.
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRect(-4, -postH, 8, postH);
    // Board: dark outer frame + cream fill + a soft inner highlight line (the GB double
    // border), all matching the message-box chrome so the seam reads as authored UI.
    gfx.fillStyle(COLORS.boxBorder, 1);
    gfx.fillRoundedRect(-w / 2 - 3, boardCy - h / 2 - 3, w + 6, h + 6, RADIUS.sm + 2);
    gfx.fillStyle(COLORS.boxFill, 1);
    gfx.fillRoundedRect(-w / 2, boardCy - h / 2, w, h, RADIUS.sm);
    gfx.lineStyle(1.5, COLORS.boxBorderInner, 1);
    gfx.strokeRoundedRect(-w / 2 + 2, boardCy - h / 2 + 2, w - 4, h - 4, RADIUS.sm);
    label.setPosition(0, boardCy);

    container.add([gfx, label]);

    // Tap the whole sign (board + post) to walk out — touch/mouse parity with walking into
    // the band. Guarded by the same depth-0 world-tap guard the greet/ground taps use, so a
    // higher control over it (never, since it's centred clear of the toggle/d-pad) can't
    // double-fire. The keyboard walks into the band; no separate key needed.
    const hitH = h + postH;
    container.setSize(w, hitH);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, boardCy - h / 2, w, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    container.input!.cursor = 'pointer';
    container.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (!this.isTopHit(pointer, container)) return;
      this.travelThroughSeam();
    });

    this.seamSign = container;
  }

  /**
   * The centred bottom trigger band (feet coords): the lower strip of the walkable ground,
   * NARROWED to the middle so it stays clear of the bottom-left menu toggle + the bottom-
   * right d-pad. Walking (feet) into it walks out to the meadow. Null while the ground is
   * un-sized.
   */
  private seamBandRect(): { x: number; y: number; w: number; h: number } | null {
    const g = this.groundRect;
    if (g.width < 60 || g.height < 60) return null;
    const w = Math.min(Math.max(220, g.width * 0.5), g.width - 96);
    return { x: g.x + g.width / 2 - w / 2, y: g.y + g.height - SEAM_BAND_H, w, h: SEAM_BAND_H };
  }

  /** Are the PLAYER's feet inside the centred bottom exit band right now? */
  private playerInSeamBand(): boolean {
    const b = this.seamBandRect();
    const p = this.player;
    if (!b || !p) return false;
    const { x, y } = p.container;
    return x >= b.x && x <= b.x + b.w && y >= b.y;
  }

  /**
   * Seat the signpost centred over the exit band, but LIFTED clear of the bottom UI cluster
   * so the visible sign never grazes a control: the invisible trigger band stays at the very
   * bottom (walking into it under the d-pad is exactly how you leave), while the sign floats
   * ABOVE the touch d-pad (bottom-right) and the OPEN nav drawer (bottom-centre on desktop).
   * The board hangs above the post, so clearing the post base clears the whole sign.
   */
  private positionSeam(): void {
    const sign = this.seamSign;
    if (!sign) return;
    const b = this.seamBandRect();
    if (!b) {
      sign.setVisible(false);
      return;
    }
    sign.setVisible(true);
    const g = this.groundRect;
    // Default: plant the post base just inside the band's top.
    let y = b.y + 22;
    // Lift above whichever bottom-cluster controls are live so the sign stays fully visible.
    const clearAbove: number[] = [];
    // The d-pad seats itself bottom-right; its top extent is size*1.5+margins up from the
    // ground bottom (DPad.layout) — lift above that when the (touch) pad is shown.
    if (this.dpad?.isVisible) clearAbove.push(g.y + g.height - 192);
    // The nav drawer, when OPEN, rests over the bottom-centre — lift above its top edge.
    if (this.menuOpen && this.drawerRect.width > 0) clearAbove.push(this.drawerRect.y);
    if (clearAbove.length > 0) {
      y = Math.min(y, Math.min(...clearAbove) - SPACING.md);
    }
    // Never rise into the wall band at the very top of the room.
    y = Math.max(y, g.y + 56);
    sign.setPosition(b.x + b.w / 2, y);
  }

  /**
   * A town spawn point at a given edge (feet coords) for arriving from a route. Only 'down'
   * (the bottom, by the signpost) is used today — walking up out of the meadow lands you
   * there; the others round out the contract for future seams.
   */
  private townEdgeSpawn(edge: Edge): { x: number; y: number } {
    const g = this.groundRect;
    const m = 34;
    switch (edge) {
      case 'down':
        return { x: g.x + g.width / 2, y: g.y + g.height - m };
      case 'up':
        return { x: g.x + g.width / 2, y: g.y + m };
      case 'left':
        return { x: g.x + m, y: g.y + g.height / 2 };
      case 'right':
        return { x: g.x + g.width - m, y: g.y + g.height / 2 };
    }
  }

  /**
   * The per-frame seam check (called from onUpdate after the player steps). Debounced: after
   * arriving from a route the player spawns INSIDE the band with the seam disarmed, so they
   * must walk UP out of it first (which arms it); walking back down then walks out. Never
   * fires mid-navigation or while an overlay (greet/cast/aim) is up.
   */
  private updateSeam(): void {
    if (this.seamTriggering || this.busy || this.greetDialog || this.castMenu || this.aimMove) {
      return;
    }
    const inBand = this.playerInSeamBand();
    if (!this.seamArmed) {
      if (!inBand) this.seamArmed = true; // player left the band → re-arm
      return;
    }
    if (inBand) this.travelThroughSeam();
  }

  /**
   * Walk out of Town through the bottom seam to the meadow. Persists the arrival position
   * (the meadow's OPPOSITE edge — its top — so it lines up with the route's own edge spawn)
   * FIRST, then hands off with the standard fade. entryEdge = OPPOSITE of Town's exit edge,
   * matching the sibling RouteScene's {nodeId, entryEdge} spawn contract.
   */
  private travelThroughSeam(): void {
    if (this.seamTriggering || this.busy) return;
    const link = this.seamLink ?? ({ toArea: 'node-meadow', edge: 'down' } as AreaLink);
    this.seamTriggering = true;
    this.busy = true;
    const entryEdge = OPPOSITE[link.edge];
    const spawn = spawnAtEdge(entryEdge, ROUTE_WORLD_W, ROUTE_WORLD_H);
    try {
      this.session.saveOverworldPos({
        area: link.toArea,
        x: spawn.x,
        y: spawn.y,
        facing: spawn.facing,
      });
    } catch {
      // Persistence is best-effort; a hiccup must never strand the player mid-walk.
    }
    if (!this.scene.isActive()) return;
    goTo(this, 'Route', { data: { nodeId: link.toArea, entryEdge } });
  }

  // ── the populated town (LOD: detailed regulars + ambient crowd + player) ──────

  private async populateTown(): Promise<void> {
    // Pull the trainer manifest first so each NPC gets a deterministic, varied sprite
    // (the town reads as a real crowd). Non-throwing: an empty manifest just falls back
    // to the default trainer key inside trainerKeyForSimTrainer.
    await this.loadTrainerKeys();
    if (!this.scene.isActive()) return;

    const society = this.session.society;
    const here = society.filter((t) => t.homeNodeId === TOWN_NODE);
    const elsewhere = society.filter((t) => t.homeNodeId !== TOWN_NODE);

    // Phone LOD: a small screen gets fewer detailed + crowd walkers so the snug room
    // reads cleanly rather than as a pile of sprites jostling the header + menu pills.
    const phone = this.phoneMode;
    const maxDetailed = phone ? PHONE_MAX_DETAILED : MAX_DETAILED;
    const maxCrowd = phone ? PHONE_MAX_CROWD : MAX_CROWD;

    // Full-detail cast: the regulars present in town (then any regulars), labeled.
    const regularsHere = here.filter((t) => t.lod === 'regular');
    const detailed = [
      ...regularsHere,
      ...society.filter((t) => t.lod === 'regular' && !regularsHere.includes(t)),
    ].slice(0, maxDetailed);

    for (const trainer of detailed) {
      const walker = this.spawnResident(trainer, true);
      this.walkers.push(walker);
      this.spawnNpcFollower(walker, trainer);
    }

    // Ambient crowd: a rotating cast of townsfolk, no labels (still real trainers).
    // The sprites now render ~2× larger, so we thin the far crowd a touch and spread
    // them out (below) — a lively town, never an overcrowded shuffle.
    const crowdPool = [...here.filter((t) => !detailed.includes(t)), ...elsewhere];
    const crowdCount = Math.min(maxCrowd, crowdPool.length);
    for (let i = 0; i < crowdCount; i++) {
      const walker = this.spawnResident(crowdPool[i], false);
      walker.container.setAlpha(0.85);
      this.walkers.push(walker);
      this.spawnNpcFollower(walker, crowdPool[i]);
    }

    // The player — a real TRAINER avatar — plus their chosen team Pokemon following.
    this.player = this.spawnPlayer();
    this.walkers.push(this.player);

    // Arm the exit seam unless the player spawned INSIDE the band (arriving from a route, or
    // a random spawn that happened to land there) — then it stays disarmed until they leave,
    // so we never bounce straight back out the way we came in.
    this.seamArmed = !this.playerInSeamBand();

    const followerSpeciesId = this.session.follower?.speciesId ?? null;
    if (followerSpeciesId) {
      this.follower = this.spawnFollower(this.player);
      // The player's follower must be in `walkers` or onUpdate never steps it (NPC
      // followers are pushed in spawnNpcFollower) — without this it strands at spawn.
      this.walkers.push(this.follower);
      void this.dressFollower(this.follower, followerSpeciesId);
      // The player's follower is the cast affordance: tap it to play one of its moves
      // into thin air (pure fun, no battle). A soft "✦ Cast" hint floats over it.
      this.makeFollowerCastable(this.follower);
    }

    // Now that the cast is present, light the "✦" over any resident with a quest to share.
    this.refreshQuestMarkers();
  }

  /**
   * Make the PLAYER's follower a tappable "cast a move" affordance + add a soft floating
   * "✦ Cast" hint so it's discoverable. Tapping it (when it's the genuine top-most hit —
   * the same depth-0 guard the greet/ground taps use) opens the move-cast menu. Touch +
   * mouse here; the C key gives keyboard parity. Pure delight: no stakes, repeatable.
   */
  private makeFollowerCastable(follower: Walker): void {
    const c = follower.container;
    // The follower container is rendered at FOLLOWER_CONTAINER_SCALE (zoomed), and the
    // hit-area is defined in the container's LOCAL (pre-scale) space — so a small local
    // rect becomes a comfortably-thumbable on-screen target around the chibi without
    // sprawling over neighbouring walkers.
    const hitW = 34;
    const hitH = 38;
    c.setSize(hitW, hitH);
    c.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH + 4, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    c.input!.cursor = 'pointer';
    c.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      // Same world-tap guard as greet/ground: only act when the follower is the genuine
      // top-most interactive object (a higher-depth control over it owns the tap instead).
      if (!this.isTopHit(pointer, c)) return;
      this.openFollowerMenu();
    });

    // A quiet, gently pulsing prompt over the follower's head so the player discovers BOTH
    // affordances (care for the partner, or show off a move). Positioned each frame in
    // onUpdate (it's scene-level, like the labels).
    this.castHint = this.add
      .text(c.x, c.y, '♥ Care · ✦ Cast', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.accent,
        fontStyle: 'bold',
        stroke: CSS.bg,
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(NAME_LABEL_DEPTH + 1);
    if (!this.reduced) {
      this.tweens.add({
        targets: this.castHint,
        alpha: { from: 0.55, to: 1 },
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Give an NPC resident THEIR lead Pokemon as a trailing PMD chibi — the same warm
   * "a partner walks beside you" read the player gets. The lead species is chosen
   * deterministically from the NPC's society team (pickLeadSpeciesId), so the same
   * resident always strolls with the same partner. Sprite-pooled by dex (the resolver
   * pools), so a town full of followers slices each species once and stays smooth.
   */
  private spawnNpcFollower(leader: Walker, trainer: SimTrainer): void {
    const speciesId = pickLeadSpeciesId(trainer);
    if (!speciesId) return;
    const follower = this.spawnFollower(leader, leader.container.alpha);
    this.walkers.push(follower);
    void this.dressFollower(follower, speciesId);
  }

  /** Fetch the trainer-sprite manifest (the available keys); never throws. */
  private async loadTrainerKeys(): Promise<void> {
    try {
      const res = await fetch(trainersManifestUrl());
      if (!res.ok) return;
      const manifest = (await res.json()) as { keys?: string[] };
      if (Array.isArray(manifest.keys)) this.trainerKeys = manifest.keys;
    } catch {
      // No manifest (offline / thin build): NPCs fall back to the default sprite.
    }
  }

  /** Create a resident as a real TRAINER avatar at a random spot on the town ground. */
  private spawnResident(trainer: SimTrainer, labeled: boolean): Walker {
    const container = this.add.container(0, 0);

    // Each NPC is a real Pokemon-game trainer, keyed deterministically off their id so
    // they always wear the same sprite and the crowd reads as varied. The avatar's
    // bottom-centre sprite is added at the container origin (feet on the container pos).
    const footprintSide = labeled ? this.residentLabeledSide : this.residentCrowdSide;
    const key = trainerKeyForSimTrainer(trainer.id, this.trainerKeys);
    const avatar = makePlayerTrainer(this, key, 0, 0);
    this.fitTrainer(avatar, footprintSide);
    container.add(avatar.sprite);

    const start = this.randomGroundPoint();
    container.setPosition(start.x, start.y);

    let label: Phaser.GameObjects.Text | undefined;
    if (labeled) {
      // The name tag lives at SCENE level (NOT inside the container) at a depth above all
      // walker sprites, so a neighbour's now-larger sprite can never draw over it (the
      // "labels sometimes don't show" bug). It's positioned every frame to ride above this
      // walker's head (see positionLabels), seeded here so it's correct on frame one.
      label = this.add
        .text(start.x, start.y - footprintSide - NAME_LABEL_GAP, trainer.username, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
        })
        .setOrigin(0.5)
        .setDepth(NAME_LABEL_DEPTH);
    }

    const hitW = footprintSide;
    const hitH = hitW + 8;
    container.setSize(hitW, hitH);

    // Tapping a trainer greets them / opens a trade with a regular. The hit area grows
    // with the (now larger) sprite so a tired thumb lands cleanly.
    container.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    container.input!.cursor = 'pointer';
    // Pass the pointer + this container so approachTrainer can confirm the walker is the
    // GENUINE top-most hit before greeting. Top-only hit-testing already means a higher-depth
    // control over this walker should win the tap on its own; the guard is belt-and-suspenders
    // so an incidental greet can never latch busy and lock Set out / the menu (the "stuck in
    // Town" bug) even if some future stacking let a lower object slip through.
    container.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) =>
      this.approachTrainer(trainer, pointer, container),
    );

    return {
      trainer,
      container,
      avatar,
      label,
      footprintSide,
      // Deliberate cardinal stop-and-go wander (no floaty diagonal drift).
      wander: initWander(start.x, start.y),
      speed: DEFAULT_WANDER.speed,
      facing: 'south',
      moving: false,
      targetX: start.x,
      targetY: start.y,
    };
  }

  /** Create the player walker as a real TRAINER avatar (their chosen Showdown sprite). */
  private spawnPlayer(): Walker {
    const container = this.add.container(0, 0);
    const avatar = makePlayerTrainer(this, this.session.avatar.trainerKey, 0, 0);
    this.fitTrainer(avatar, this.playerSide);
    container.add(avatar.sprite);

    // Arriving from a route (walked up out of the meadow) seats the player at that town
    // edge — by the signpost — facing into the room; otherwise a relaxed random spot.
    const start = this.arrivalEdge
      ? this.townEdgeSpawn(this.arrivalEdge)
      : this.randomGroundPoint();
    const facing: Direction =
      this.arrivalEdge === 'down'
        ? 'north'
        : this.arrivalEdge === 'up'
          ? 'south'
          : this.arrivalEdge === 'left'
            ? 'east'
            : this.arrivalEdge === 'right'
              ? 'west'
              : 'south';
    container.setPosition(start.x, start.y);
    container.setSize(this.playerSide, this.playerSide + 8);

    // The player's own name tag is SCENE-level too (above every sprite + above resident
    // tags, so it always reads), positioned each frame to ride over the player's head.
    const label = this.add
      .text(start.x, start.y - this.playerSide - NAME_LABEL_GAP, this.session.trainerName, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.accent,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(NAME_LABEL_DEPTH + 1);

    return {
      trainer: null,
      container,
      avatar,
      label,
      footprintSide: this.playerSide,
      targetX: start.x,
      targetY: start.y,
      speed: 70, // unhurried stroll toward tapped destinations
      facing,
      moving: false,
    };
  }

  /**
   * Scale a trainer sprite so its tallest side fits the friendly town footprint. The
   * avatar starts on a small placeholder and swaps in the (larger) real Showdown PNG
   * after it decodes, so we re-fit a few times over the next moment to catch the swap
   * — the sprite never ends up oversized once the real art lands.
   */
  private fitTrainer(avatar: TrainerAvatar, maxSide: number): void {
    const fit = (): void => {
      const s = avatar.sprite;
      if (!s.active) return;
      const w = s.texture.getSourceImage().width || s.width || 1;
      const h = s.texture.getSourceImage().height || s.height || 1;
      s.setScale(Math.min(maxSide / w, maxSide / h, SPRITE_SCALE_CAP));
    };
    fit();
    // Re-fit once the real PNG likely decoded (placeholder → larger texture). Cheap.
    this.time.delayedCall(120, fit);
    this.time.delayedCall(400, fit);
    this.time.delayedCall(1000, fit);
  }

  /**
   * The lead Pokémon (PMD chibi) that trails a leader (the player or an NPC) a step
   * behind, walking the same deliberate cardinal path. It owns no wander state — it
   * just follows breadcrumbs of its leader (see stepFollowerWalker).
   */
  private spawnFollower(leader: Walker, alpha = 1): Walker {
    const container = this.add.container(0, 0);
    const placeholder = this.makePlaceholderBlob(COLORS.warm);
    container.add(placeholder);
    // Spawn BESIDE the leader (a tile to its left) so it reads as a partner already at
    // the leader's heel from frame one — never stranded across the room (the Route fix).
    const start = { x: leader.container.x - FOLLOW_GAP, y: leader.container.y };
    container.setPosition(start.x, start.y);
    container.setAlpha(alpha);
    // The follower chibi renders larger for the zoomed-in tavern, so the placeholder
    // blob is scaled to match the real chibi it stands in for.
    container.setScale(FOLLOWER_CONTAINER_SCALE);
    // The follower's catch-up speed sits a touch ABOVE its LEADER's walk speed (exactly
    // like the Route fix: FOLLOW_SPEED > WALK_SPEED), so it can always close any lag a
    // long walk opened and never drifts screen-distant. The old fixed 60 px/s sat BELOW
    // the player's 70 px/s stroll, which is why "Squirtle didn't follow me" in the tavern.
    const leaderSpeed = leader.speed > 0 ? leader.speed : DEFAULT_WANDER.speed;
    const follower: Walker = {
      trainer: null,
      container,
      leader,
      followGap: FOLLOW_GAP,
      targetX: start.x,
      targetY: start.y,
      speed: Math.max(leaderSpeed * FOLLOW_SPEED_FACTOR, leaderSpeed + 16),
      facing: 'south',
      moving: false,
    };
    // SEED THE LEADER'S TRAIL at the follower's spawn so stepFollowerWalker has a
    // breadcrumb to chase from the very first frame. stepFollowerWalker reads the
    // LEADER's trail (keyed by the leader), so the seed must live under the leader — not
    // the follower's own (unused) key, which was the prior dead seed.
    const ltrail = this.leaderTrail(leader);
    if (ltrail.length === 0) ltrail.push({ x: leader.container.x, y: leader.container.y });
    // Give the follower its own (head) trail point too, harmless + keeps maps populated.
    this.trails.set(follower, [{ x: start.x, y: start.y }]);
    return follower;
  }

  /** A soft rounded blob standing in until the follower's PMD chibi resolves. */
  private makePlaceholderBlob(color: number): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();
    // Origin bottom-centre to match the chibi feet-on-position convention.
    g.fillStyle(color, 1);
    g.fillRoundedRect(-12, -28, 24, 28, RADIUS.sm);
    g.fillStyle(COLORS.bg, 0.25);
    g.fillRoundedRect(-12, -8, 24, 8, RADIUS.sm);
    return g;
  }

  private async dressFollower(walker: Walker, speciesId: string): Promise<void> {
    const sp = this.index?.speciesById(speciesId);
    if (!sp) return;
    // Size the follower by its Pokedex height (vaguely-sigmoid, hard-capped at
    // 1.2× the trainer): a Caterpie trails small, a Snorlax looms — never bigger
    // than the trainer. Missing/invalid height falls back to a sensible default.
    const sizeFactor =
      typeof sp.height === 'number' && sp.height > 0
        ? followerScale(sp.height)
        : FOLLOWER_SCALE_DEFAULT;
    // Remember the height so a relayout can re-fit this follower against its leader's
    // NEW (phone-shrunk or desktop) footprint without re-reading the species index.
    walker.dexHeight = sp.height;
    // Size + cap against THIS follower's OWN leader's footprint (the player at the live
    // playerSide, an NPC at its resident side) — never always the player's.
    const leaderSide = walker.leader?.footprintSide ?? this.playerSide;
    await this.attachActor(walker, sp.dexNo, followerScaleCap(leaderSide, sizeFactor));
  }

  /**
   * Build a correctly-SLICED directional PMD chibi for `dexNo` and swap it in for the
   * placeholder blob. Pools by dex via the Foundation resolver, so a busy town slices
   * each species once. Fitted to a friendly chibi size; never throws.
   */
  private async attachActor(walker: Walker, dexNo: number, scaleCap: number): Promise<void> {
    const ow = await this.resolver.overworldSprite(dexNo);
    if (!this.scene.isActive()) return;
    const actor = createOverworldActor(this, ow, 0, 0);
    // Fit to the STABLE nominal frame size (constant across idle + walk frames), so the
    // rendered footprint stays put through the walk cycle instead of ballooning.
    fitOverworldActor(actor, FOLLOWER_FIT_BASE * scaleCap, SPRITE_SCALE_CAP);
    actor.face(walker.facing);
    walker.actor = actor;
    walker.container.add(actor.sprite);
    // Hide the placeholder blob now that a real chibi is shown.
    const blob = walker.container.list[0];
    if (blob instanceof Phaser.GameObjects.Graphics) blob.setVisible(false);
  }

  /** A friendly display name for a species (falls back to the raw id). */
  private speciesLabel(speciesId: string): string {
    return this.index?.speciesById(speciesId)?.name ?? speciesId;
  }

  /**
   * Greet a trainer with the classic Pokémon chrome: a cream DialogBox with a warm,
   * in-character line, and (for regulars) a ▶-cursor choice Menu — Friendly battle /
   * Propose trade / Just chat. Dip-in or ignore: "Just chat" simply closes warmly. A
   * passing crowd member gives a friendly one-line hello (no menu). Tasteful GB/GBC nod.
   */
  private approachTrainer(
    trainer: SimTrainer,
    pointer: Phaser.Input.Pointer,
    container: Phaser.GameObjects.Container,
  ): void {
    if (this.busy || this.greetDialog) return;
    // THE GUARD (the "stuck in Town" fix), belt-and-suspenders over default top-only
    // hit-testing. Top-only already means a higher-depth UI control (the menu toggle/drawer
    // button, "Set out", a chat panel/row, a modal scrim) sitting OVER this walker wins the
    // tap; this re-checks it, only greeting when this walker's container is the GENUINE
    // top-most interactive object under the pointer. Otherwise the control owns the tap and
    // we stay silent — so a control tap can never open an incidental greet that would latch
    // busy and lock setOut / the menu / entering a building.
    if (!this.isTopHit(pointer, container)) return;

    const warm =
      trainer.friendshipWithPlayer >= 40 ? 'waves you over warmly' : 'gives a friendly nod';

    // A passing crowd member (not a full regular) just shares a warm hello.
    if (trainer.lod !== 'regular') {
      new Toast(this, this.layout.centerX, this.layout.safe.y + 80, {
        text: `${trainer.username} ${warm}.`,
        reducedMotion: this.reduced,
        accent: COLORS.warm,
        width: Math.min(360, this.layout.safe.width - SPACING.lg * 2),
      });
      return;
    }

    // A regular: open the classic greeting box + choice menu.
    this.busy = true;
    // Fire the "talk-to" quest event (a warm hello is a real objective for some quests).
    this.quests.greet(trainer.id);
    const safe = this.layout.safe;
    const w = Math.min(560, safe.width - SPACING.lg);
    const h = 116;

    // A soft scrim so the conversation reads clearly over the lively town.
    this.greetScrim = this.add
      .rectangle(
        this.layout.centerX,
        this.layout.centerY,
        this.layout.width,
        this.layout.height,
        COLORS.scrim,
        0.4,
      )
      .setOrigin(0.5)
      .setDepth(200)
      .setInteractive();

    this.greetDialog = new DialogBox(
      this,
      this.layout.centerX,
      safe.y + safe.height - h / 2 - SPACING.md,
      {
        width: w,
        height: h,
        reducedMotion: this.reduced,
      },
    );
    this.greetDialog.setDepth(201);
    this.greetDialog.show(
      [`${trainer.username} ${warm}.`, townGreetingLine(trainer)],
      trainer.username,
    );

    this.openGreetMenu(trainer);
  }

  /** Build the ▶-cursor choice Menu for a regular's greeting, seated above the dialog. */
  private openGreetMenu(trainer: SimTrainer): void {
    const safe = this.layout.safe;
    const dialogTop = safe.y + safe.height - 116 - SPACING.md;

    const close = (): void => this.closeGreeting();

    const items: MenuItem[] = [];
    // "What's on your mind?" surfaces this regular's offerable / turn-in-ready quest — only
    // shown when they actually have one, so the menu stays clean for a casual hello.
    const quest = this.relevantQuestFor(trainer.id);
    if (quest) {
      items.push({
        label: 'What’s on your mind?',
        hint: '✦',
        onSelect: () => this.openQuestDialog(trainer, quest),
      });
    }
    items.push(
      { label: 'Friendly battle', onSelect: () => this.startTownFriendlyBattle(trainer) },
      { label: 'Propose trade', onSelect: () => this.openTownTrade(trainer) },
      { label: 'Just chat', onSelect: close },
    );

    this.greetMenu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(280, safe.width - SPACING.lg * 2),
      items,
      onCancel: close,
    });
    this.greetMenu.setDepth(202);
    // Sit the menu just above the dialog box, centered.
    this.greetMenu.setPosition(
      this.layout.centerX,
      dialogTop - this.greetMenu.boxHeight / 2 - SPACING.sm,
    );
    this.greetMenu.focus();
  }

  /** Tear down the greeting overlay + return the player to calm pottering. */
  private closeGreeting(): void {
    this.greetMenu?.destroy();
    this.greetMenu = undefined;
    this.greetDialog?.destroy();
    this.greetDialog = undefined;
    this.greetScrim?.destroy();
    this.greetScrim = undefined;
    this.busy = false;
    // A greet may have accepted/turned-in a quest — recompute the "✦" markers.
    this.refreshQuestMarkers();
  }

  // ── quest givers: soft "✦" markers + the offer / turn-in conversation ──────────────

  private get quests(): QuestSurface {
    return questView(this.session);
  }

  /** The read-only world facts the quest availability gates consult (visibility only). */
  private questWorldSlice(): QuestWorldSlice {
    const st = this.quests.questState;
    return {
      badges: st.badges,
      completedQuestIds: st.completed,
      caughtCount: this.session.dex.caught.length,
      friendshipWith: (id: string) =>
        this.session.society.find((t) => t.id === id)?.friendshipWithPlayer ?? 0,
    };
  }

  /**
   * The quest (if any) this resident wants to talk about — a TURN-IN takes priority over a
   * fresh OFFER (main before side). Pure visibility read; returns null when they've nothing.
   */
  private relevantQuestFor(
    trainerId: string,
  ): { phase: 'offer' | 'turn-in'; def: QuestDef } | null {
    const v = this.quests;
    const st = v.questState;
    // Turn-in: a completed-not-turned-in quest whose giver resolves to this resident.
    for (const p of st.active) {
      if (p.turnedIn) continue;
      const def = v.questDefs.find((d) => d.id === p.questId);
      if (
        def &&
        this.session.questGiverId(def) === trainerId &&
        isReadyToTurnIn(def, p.stageIndex, p.turnedIn)
      ) {
        return { phase: 'turn-in', def };
      }
    }
    // Offer: an offerable quest this resident gives (main sorts before side).
    const offers = offerableQuests(st, v.questDefs, this.questWorldSlice())
      .filter((d) => this.session.questGiverId(d) === trainerId)
      .sort((a, b) => (a.kind === 'main' ? 0 : 1) - (b.kind === 'main' ? 0 : 1));
    if (offers[0]) return { phase: 'offer', def: offers[0] };
    return null;
  }

  /**
   * Recompute which residents deserve a soft "✦" overhead marker (an offerable or
   * turn-in-ready quest) and create/destroy the marker text accordingly. Cheap: one pass
   * over the small labeled cast. Called on enter + whenever a greet closes.
   */
  private refreshQuestMarkers(): void {
    if (!this.scene.isActive()) return;
    const ids = new Set<string>();
    const v = this.quests;
    const st = v.questState;
    for (const p of st.active) {
      if (p.turnedIn) continue;
      const def = v.questDefs.find((d) => d.id === p.questId);
      if (def && isReadyToTurnIn(def, p.stageIndex, p.turnedIn))
        ids.add(this.session.questGiverId(def));
    }
    for (const def of offerableQuests(st, v.questDefs, this.questWorldSlice()))
      ids.add(this.session.questGiverId(def));
    this.questGiverIds = ids;

    for (const w of this.walkers) {
      const id = w.trainer?.id;
      const wants = !!id && ids.has(id) && !!w.label; // only the labeled regular cast
      if (wants && !w.questMarker) {
        w.questMarker = this.add
          .text(w.container.x, w.container.y, '✦', {
            fontFamily: FONT.family,
            fontSize: `${FONT.size.md}px`,
            color: CSS.warm,
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(NAME_LABEL_DEPTH + 1);
      } else if (!wants && w.questMarker) {
        w.questMarker.destroy();
        w.questMarker = undefined;
      }
    }
    this.positionLabels();
  }

  /**
   * The quest OFFER / TURN-IN conversation, opened from the greet menu's "What's on your
   * mind?" item. Reuses the greet overlay (scrim + cream dialog + ▶-cursor menu, the modal
   * 200+ band): the giver's authored lines fill the DialogBox and a focusable Menu drives
   * the choice — so it works by touch AND full keyboard, exactly like the greet menu.
   *
   * OFFER  → stage-0 onStart lines + Accept / Maybe later (Accept → session.acceptQuest).
   * TURN-IN → final onComplete lines + a warm confirm (→ session.turnInQuest + reward line).
   * Never a wall: declining costs nothing and the quest simply waits.
   */
  private openQuestDialog(
    trainer: SimTrainer,
    quest: { phase: 'offer' | 'turn-in'; def: QuestDef },
  ): void {
    this.greetMenu?.destroy();
    this.greetMenu = undefined;
    const def = quest.def;
    const giver = trainer.username;
    const safe = this.layout.safe;
    const dialogTop = safe.y + safe.height - 116 - SPACING.md;
    const close = (): void => this.closeGreeting();

    let lines: string[];
    let items: MenuItem[];
    if (quest.phase === 'turn-in') {
      const last = def.stages[def.stages.length - 1];
      lines = last ? [...last.dialogue.onComplete] : ['Thank you — truly.'];
      items = [
        {
          label: 'Here you go!',
          onSelect: () => {
            this.quests.turnInQuest(def.id);
            this.townQuestToast(
              rewardLine(def.reward, { speciesName: (id) => this.index?.speciesById(id)?.name }),
            );
            this.closeGreeting();
          },
        },
      ];
    } else {
      const first = def.stages[0];
      lines = first ? [...first.dialogue.onStart] : [def.synopsis];
      items = [
        {
          label: 'I’m in!',
          onSelect: () => {
            this.quests.acceptQuest(def.id);
            this.townQuestToast('Added to your Quest Log — take your time, no rush at all.');
            this.closeGreeting();
          },
        },
        { label: 'Maybe later', onSelect: close },
      ];
    }

    this.greetDialog?.show(lines, giver);
    this.greetMenu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(280, safe.width - SPACING.lg * 2),
      items,
      onCancel: close,
    });
    this.greetMenu.setDepth(202);
    this.greetMenu.setPosition(
      this.layout.centerX,
      dialogTop - this.greetMenu.boxHeight / 2 - SPACING.sm,
    );
    this.greetMenu.focus();
  }

  /** A soft warm toast near the top — quest accept / reward confirmations. */
  private townQuestToast(text: string): void {
    new Toast(this, this.layout.centerX, this.layout.safe.y + 80, {
      text,
      reducedMotion: this.reduced,
      accent: COLORS.warm,
      width: Math.min(400, this.layout.safe.width - SPACING.lg * 2),
    });
  }

  // ── tavern move casting (cast a move into thin air, for the joy of it) ─────────

  /** The lead/follower mon whose moves the player casts — the live follower instance. */
  private castMon(): PokemonInstance | null {
    return this.session.follower ?? this.session.getTeam()[0] ?? null;
  }

  /**
   * Open the ▶-cursor menu of the follower's castable moves (a cream choice list above a
   * soft scrim, mirroring the greet menu's chrome). Choosing one enters FREE-AIM cast mode
   * (tap anywhere to fire toward that spot, re-castable); cancel just closes. Guards: never
   * while busy / another overlay is open / the menu is already up / aiming / an effect is
   * mid-flight. Falls back to a gentle toast when the lead has no resolvable moves, so the
   * player never meets an empty menu.
   */
  private openCastMenu(): void {
    if (this.busy || this.casting || this.aimMove || this.greetDialog || this.castMenu) return;
    if (!this.scene.isActive()) return;

    const mon = this.castMon();
    const moves = castableMoves(mon, (id) => this.index?.moveById(id));
    if (moves.length === 0) {
      new Toast(this, this.layout.centerX, this.layout.safe.y + 80, {
        text: 'Your partner has no moves to show off just yet.',
        reducedMotion: this.reduced,
        accent: COLORS.warm,
        width: Math.min(360, this.layout.safe.width - SPACING.lg * 2),
      });
      return;
    }

    this.busy = true;
    const safe = this.layout.safe;

    // A soft scrim so the menu reads clearly over the lively room; tap it to cancel.
    this.castScrim = this.add
      .rectangle(
        this.layout.centerX,
        this.layout.centerY,
        this.layout.width,
        this.layout.height,
        COLORS.scrim,
        0.4,
      )
      .setOrigin(0.5)
      .setDepth(MODAL_DEPTH)
      .setInteractive();
    this.castScrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeCastMenu());

    const close = (): void => this.closeCastMenu();
    const items: MenuItem[] = moves.map((m) => ({
      label: m.name,
      onSelect: () => this.enterCastAimMode(m),
    }));
    items.push({ label: 'Never mind', onSelect: close });

    this.castMenu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(260, safe.width - SPACING.lg * 2),
      items,
      onCancel: close,
    });
    this.castMenu.setDepth(MODAL_DEPTH + 2);
    this.castMenu.setPosition(this.layout.centerX, this.layout.centerY);
    this.castMenu.focus();
  }

  /**
   * The two-item follower menu shown when you TAP your partner: ♥ Care (the cozy care room)
   * or ✦ Show off a move (the existing cast flow). Both reachable by touch; reuses the cast
   * menu's scrim/menu fields so teardown is shared. Guards like openCastMenu.
   */
  private openFollowerMenu(): void {
    if (this.busy || this.casting || this.aimMove || this.greetDialog || this.castMenu) return;
    if (!this.scene.isActive()) return;
    this.busy = true;

    const safe = this.layout.safe;
    this.castScrim = this.add
      .rectangle(
        this.layout.centerX,
        this.layout.centerY,
        this.layout.width,
        this.layout.height,
        COLORS.scrim,
        0.4,
      )
      .setOrigin(0.5)
      .setDepth(MODAL_DEPTH)
      .setInteractive();
    this.castScrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeCastMenu());

    const items: MenuItem[] = [
      {
        label: '♥ Care',
        onSelect: () => {
          this.closeCastMenu();
          this.openCareForFollower();
        },
      },
      {
        label: '✦ Show off a move',
        onSelect: () => {
          // Tear the follower menu down WITHOUT the closeCastMenu busy-release race, then
          // open the real cast menu (which re-latches busy).
          this.castMenu?.destroy();
          this.castMenu = undefined;
          this.castScrim?.destroy();
          this.castScrim = undefined;
          this.busy = false;
          this.openCastMenu();
        },
      },
      { label: 'Never mind', onSelect: () => this.closeCastMenu() },
    ];

    this.castMenu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(260, safe.width - SPACING.lg * 2),
      items,
      onCancel: () => this.closeCastMenu(),
    });
    this.castMenu.setDepth(MODAL_DEPTH + 2);
    this.castMenu.setPosition(this.layout.centerX, this.layout.centerY);
    this.castMenu.focus();
  }

  /** Leave Town for the cozy Care room for the current follower; returns to Town on Back. */
  private openCareForFollower(): void {
    if (!this.scene.isActive()) return;
    const id = this.session.follower?.id;
    this.busy = true; // the scene is leaving
    goTo(this, 'Care', { data: { instanceId: id, returnScene: 'Town' } });
  }

  /**
   * A brief, warm reaction over the trailing follower after a care act (on return from the
   * Care room): a few hearts float up + a gentle hop, and — throttled, ambient — a
   * `follower-mood` beat drifts into town chat. Reduced-motion shows just the hearts fading.
   */
  private reactFollowerMood(emotion: Emotion): void {
    const f = this.follower;
    if (!f || !f.container.active) return;
    const cx = f.container.x;
    const cy = f.container.y - 40;

    const glyph = emotion === 'joyous' ? '✨' : emotion === 'sigh' ? '☁' : '♥';
    const count = this.reduced ? 1 : 3;
    for (let i = 0; i < count; i++) {
      const heart = this.add
        .text(cx + (i - 1) * 12, cy, glyph, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.warm,
          stroke: CSS.bg,
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(130);
      if (this.reduced) {
        this.time.delayedCall(700, () => heart.destroy());
      } else {
        this.tweens.add({
          targets: heart,
          y: cy - 46 - i * 6,
          alpha: { from: 1, to: 0 },
          duration: 950 + i * 120,
          ease: 'Sine.easeOut',
          onComplete: () => heart.destroy(),
        });
      }
    }
    if (!this.reduced) {
      this.tweens.add({
        targets: f.container,
        y: f.container.y - 6,
        duration: 150,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }

    // Ambient, throttled town-chat beat (warm by construction; swallowed on any error).
    try {
      const mon = this.session.follower;
      if (mon) {
        this.session.emitFollowerMoodBeat?.(TOWN_CHANNEL, mon.nickname ?? mon.speciesId, emotion);
      }
    } catch {
      /* a mood beat is pure warmth, never load-bearing */
    }
  }

  /** Tear down the cast menu + scrim and return to calm pottering (keeps any live VFX). */
  private closeCastMenu(): void {
    this.castMenu?.destroy();
    this.castMenu = undefined;
    this.castScrim?.destroy();
    this.castScrim = undefined;
    // Reached only on cancel (Never mind / scrim tap) — picking a move enters aim mode
    // instead. Release busy unless an effect is somehow still resolving (its finally clears
    // casting); aim mode owns its own busy/cleanup via exitCastAimMode.
    if (!this.casting) this.busy = false;
  }

  /**
   * Enter FREE-AIM cast mode for `move`: tear down the move menu, then show a light "cast
   * mode" — a reticle (seeded out in front of the follower), a "Tap anywhere to cast
   * <Move>" prompt, and a "✕ Done" chip. The NEXT tap ANYWHERE in the room sets the target
   * and fires the move FROM the follower TOWARD it; we STAY in the mode so the player can
   * keep tapping new spots to re-cast. Keyboard parity: arrows nudge the reticle, Enter
   * fires at it, Esc exits. Touch-first, re-entry safe, respects reduced motion.
   */
  private enterCastAimMode(move: CastableMove): void {
    // Drop the move menu chrome; cast mode replaces it.
    this.castMenu?.destroy();
    this.castMenu = undefined;
    this.castScrim?.destroy();
    this.castScrim = undefined;

    const caster = this.follower ?? this.player;
    if (!caster || !this.scene.isActive()) {
      this.busy = false;
      return;
    }

    this.aimMove = move;
    this.busy = true; // latched: cast mode owns the scene until Done/Esc.

    // Seed the reticle a sensible distance out in front of the follower (the old fixed
    // target), so the first frame already shows a believable aim point to tweak or fire at.
    const from = { x: caster.container.x, y: caster.container.y };
    const facing = toCardinalFacing(caster.facing);
    this.aimPoint = castTargetPoint(from, facing, this.groundRect, CAST_REACH, CAST_LIFT);

    // A full-room transparent capture layer over the ground: ANY tap on it is an aim+fire.
    const g = this.groundRect;
    this.aimLayer = this.add
      .rectangle(g.x + g.width / 2, g.y + g.height / 2, g.width, g.height, 0x000000, 0.001)
      .setDepth(AIM_LAYER_DEPTH)
      .setInteractive();
    this.aimLayer.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      // Same belt-and-suspenders top-hit guard the ground/greet taps use: default top-only
      // hit-testing already gives a HIGHER control over the room (Done, the menu toggle, Set
      // out, a chat row) the tap, but re-check anyway — only aim+fire when the layer itself
      // is the genuine top hit, so those controls always own their own taps.
      if (!this.aimLayer || !this.isTopHit(pointer, this.aimLayer)) return;
      this.aimPoint = {
        x: Phaser.Math.Clamp(pointer.worldX, g.x + 12, g.x + g.width - 12),
        y: Phaser.Math.Clamp(pointer.worldY, g.y + 12, g.y + g.height - 12),
      };
      this.redrawReticle();
      void this.fireCastAtReticle();
    });

    this.aimReticle = this.add.graphics().setDepth(AIM_RETICLE_DEPTH);
    this.redrawReticle();
    if (!this.reduced) {
      this.aimReticleTween = this.tweens.add({
        targets: this.aimReticle,
        alpha: { from: 0.6, to: 1 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // The discoverability prompt + the Done chip, pinned by relayout-safe coords.
    const safe = this.layout.safe;
    this.aimPrompt = this.add
      .text(this.layout.centerX, safe.y + 56, `Tap anywhere to cast ${move.name}`, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.text,
        fontStyle: 'bold',
        backgroundColor: CSS.surface,
        padding: { x: 10, y: 6 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(AIM_PROMPT_DEPTH);

    this.aimDone = new Button(this, this.layout.centerX, safe.y + safe.height - 56, {
      label: '✕ Done',
      variant: 'warm',
      reducedMotion: this.reduced,
      onClick: () => this.exitCastAimMode(),
    });
    this.aimDone.setDepth(AIM_PROMPT_DEPTH);

    // Live keyboard: Enter fires at the reticle, Esc exits (arrows are read each frame).
    const kb = this.input.keyboard;
    if (kb) {
      this.aimEnterKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
      this.aimEscKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
      this.aimEnterKey.on('down', this.onAimEnter, this);
      this.aimEscKey.on('down', this.onAimEsc, this);
    }
  }

  /** Enter-key: fire the aimed move at the current reticle (keyboard parity with a tap). */
  private onAimEnter(): void {
    if (!this.aimMove) return;
    void this.fireCastAtReticle();
  }

  /** Esc-key: leave cast mode (keyboard parity with the Done chip). */
  private onAimEsc(): void {
    if (this.aimMove) this.exitCastAimMode();
  }

  /** Redraw the aim reticle (a ringed crosshair) at the current aim point. */
  private redrawReticle(): void {
    const r = this.aimReticle;
    if (!r) return;
    const { x, y } = this.aimPoint;
    r.clear();
    r.lineStyle(2, COLORS.accent, 0.95);
    r.strokeCircle(x, y, AIM_RETICLE_R);
    r.strokeCircle(x, y, AIM_RETICLE_R * 0.45);
    r.lineBetween(x - AIM_RETICLE_R - 5, y, x - AIM_RETICLE_R + 3, y);
    r.lineBetween(x + AIM_RETICLE_R - 3, y, x + AIM_RETICLE_R + 5, y);
    r.lineBetween(x, y - AIM_RETICLE_R - 5, x, y - AIM_RETICLE_R + 3);
    r.lineBetween(x, y + AIM_RETICLE_R - 3, x, y + AIM_RETICLE_R + 5);
  }

  /**
   * Per-frame keyboard reticle nudge while aiming: read the held arrow/WASD vector and move
   * the reticle smoothly (clamped inside the room) via the pure nudgeReticle helper, scaling
   * the step by AIM_NUDGE_STEP and dt so it glides at a steady px/sec rather than per-frame.
   * No-op when no key is held, so a still reticle stays put for a calm tap-only player.
   */
  private stepReticleNudge(dt: number): void {
    const v = this.keyboardVector();
    if (!v) return;
    const step = AIM_NUDGE_STEP * 8 * dt; // ~192 px/s glide while held
    this.aimPoint = nudgeReticle(this.aimPoint, v.x, v.y, this.groundRect, step);
    this.redrawReticle();
  }

  /**
   * Fire the currently-aimed move FROM the follower's feet TOWARD the reticle, in the move's
   * own type + category style. STAYS in cast mode so the player can keep re-casting; a tap
   * landing while an effect is still mid-flight is ignored (no stacking) but the reticle and
   * mode persist. No opponent, no battle — pure show. Respects reduced motion; safe if the
   * scene tears down mid-effect (both backends resolve promptly).
   *
   * VFX resolution mirrors BattleScene.playMoveVfx EXACTLY: when the move resolves to a
   * bespoke {@link getShowdownAnim} script, the fx textures loaded (`this.fxReady`), AND
   * we're not reduced-motion, it plays the REAL Showdown choreography via two Actors —
   * ATTACKER = the follower (the caster) at its screen position, DEFENDER = the TAPPED
   * reticle point — so Hydro Pump (etc.) reads bespoke in the tavern, not generic. Every
   * other case (no Showdown script, fx pack absent, OR reduced-motion) keeps the procedural
   * {@link playMoveEffect} fallback unchanged.
   */
  private async fireCastAtReticle(): Promise<void> {
    const move = this.aimMove;
    if (!move || this.casting) return; // ignore re-fire while one is still playing
    const caster = this.follower ?? this.player;
    if (!caster || !this.scene.isActive()) return;

    this.casting = true;
    // The caster's body anchor: lifted a little above the container feet so a beam emanates
    // from the chibi's body, not the floor (mirrors BattleScene.effectAnchor's body lift).
    const fromBody = { x: caster.container.x, y: caster.container.y - CAST_CASTER_LIFT };
    const to = { x: this.aimPoint.x, y: this.aimPoint.y };

    // Throttled NPC reaction (in parallel with the VFX): nearby residents react to the
    // flourish at most once per CAST_REACT_COOLDOWN_MS, so rapid tap-casting never spams
    // the chat or burns the LLM budget. The animation below always plays, every tap.
    this.reactToCast(move);

    try {
      // Tier 1 — the bespoke ported Showdown choreography (same resolver BattleScene uses),
      // only with the fx art loaded + full motion. The follower CASTS the real animation
      // fired from itself toward the tapped point.
      const anim =
        this.fxReady && !this.reduced ? getShowdownAnim(move.moveId, move.category) : null;
      if (anim) {
        // ATTACKER = the follower, the NEAR actor (isFrontSprite=false → logical z=NEAR_Z),
        // driven by its container so a script's attacker-lunge moves the chibi; restored to
        // rest after, belt-and-braces.
        const c = caster.container;
        const rest = { x: c.x, y: c.y };
        const attacker = makeAnchoredActor(fromBody, false, c);
        // DEFENDER = the TAPPED reticle point, the FAR actor (isFrontSprite=true → logical
        // z=FAR_Z) — a read-only anchor (no creature to lunge), so effects land on the tap
        // without moving anything.
        const defender = makeAnchoredActor(to, true, null);
        // The two Actors carry DISTINCT logical z (NEAR_Z / FAR_Z), so deriveFieldFromActors
        // FITS the field onto the two real screen anchors (follower body → tapped point): a
        // beam/projectile now STRETCHES the full path from the follower to the tap instead of
        // popping at their midpoint. Same fit battle uses, with the tap standing in for the
        // opponent. Hydro Pump = the real water blast from the follower aimed at the tap.
        try {
          await playShowdownAnim(this, anim, [attacker, defender], {
            pace: CAST_PACE,
            reduced: this.reduced,
            depth: CAST_EFFECT_DEPTH,
          });
        } finally {
          // A well-behaved script returns the attacker to rest, but restore defensively so a
          // stray lunge can never leave the follower displaced after a cast.
          if (c.active) c.setPosition(rest.x, rest.y);
        }
        return;
      }

      // Tier 2 — the procedural fallback (unchanged). Reduced-motion / no-script / no-fx land here.
      await playMoveEffect(this, {
        moveId: move.moveId,
        moveName: move.name,
        type: move.type,
        category: move.category,
        from: { x: caster.container.x, y: caster.container.y },
        to,
        reduced: this.reduced,
        pace: CAST_PACE,
        depth: CAST_EFFECT_DEPTH,
      });
    } catch {
      // Neither backend rejects by contract; belt-and-braces so a hiccup never strands the
      // casting latch and blocks re-casting / exiting.
    } finally {
      this.casting = false;
    }
  }

  /**
   * Make PRESENT residents REACT to a tavern cast — warm, impressed admiration grounded in
   * the specific move/element ("Whoa, a Flamethrower indoors?!"). Mirrors the show-off
   * surfacing path: nearby regulars amble over (nominateApproachers), their fresh lines (the
   * ones castInChat returns) bubble overhead IMMEDIATELY, and the pump is nudged so the same
   * lines stream into the town CHAT panel as rows (the show-off surfacing: popBubble + pump).
   *
   * THROTTLED: the cast ANIMATION fires every tap (the caller), but this async, token-costing
   * reaction is gated by castReactReady to at most once per CAST_REACT_COOLDOWN_MS. While the
   * cooldown is active this is a quiet no-op — the player still sees the VFX, just no new
   * chatter. Stamps the cooldown UP FRONT so concurrent rapid taps can't slip past the gate
   * before the await resolves. Safe if the scene tears down mid-await (re-checks isActive).
   */
  private reactToCast(move: CastableMove): void {
    const now = this.time.now;
    if (!castReactReady(this.castReactAt, now, CAST_REACT_COOLDOWN_MS)) return;
    this.castReactAt = now; // stamp before the await so a burst of taps shares one window

    // Draw a couple of nearby regulars over to ooh at the flourish in person (capped + gated).
    this.nominateApproachers();

    void this.session
      .castInChat(TOWN_CHANNEL, TOWN_NODE, move.moveId)
      .then((lines) => {
        if (!this.scene.isActive()) return;
        // Pop the freshly generated reactions overhead RIGHT AWAY (the living-hearth read +
        // zero display latency). We do NOT append them as chat rows here: castInChat also
        // buffers these same lines for the streaming pump, so the chat ROWS arrive when the
        // pump drains them (re-popping the bubble harmlessly — popBubbleFor replaces a
        // speaker's existing one). Appending here too would double them in the chat list.
        for (const line of lines) {
          if (line.trainerId !== 'player') this.popBubbleFor(line, false);
        }
        // Kick the pump so the buffered reaction streams into town chat promptly.
        this.nudgePump();
      })
      .catch(() => {
        /* castInChat is non-throwing by contract; belt-and-braces. */
      });
  }

  /**
   * Leave free-aim cast mode: tear down the aim layer, reticle, prompt, Done chip + the
   * live Enter/Esc keys, and release busy (unless an effect is still resolving — its
   * finally clears casting). Re-entry safe; called from Done, Esc, and teardown.
   */
  private exitCastAimMode(): void {
    this.aimMove = undefined;
    this.aimLayer?.destroy();
    this.aimLayer = undefined;
    this.aimReticleTween?.stop();
    this.aimReticleTween = undefined;
    this.aimReticle?.destroy();
    this.aimReticle = undefined;
    this.aimPrompt?.destroy();
    this.aimPrompt = undefined;
    this.aimDone?.destroy();
    this.aimDone = undefined;
    if (this.aimEnterKey) {
      this.aimEnterKey.off('down', this.onAimEnter, this);
      this.input.keyboard?.removeKey(this.aimEnterKey);
      this.aimEnterKey = undefined;
    }
    if (this.aimEscKey) {
      this.aimEscKey.off('down', this.onAimEsc, this);
      this.input.keyboard?.removeKey(this.aimEscKey);
      this.aimEscKey = undefined;
    }
    if (!this.casting) this.busy = false;
  }

  /** A no-stakes friendly spar with a regular (reuses the Battle scene). */
  private startTownFriendlyBattle(trainer: SimTrainer): void {
    const party = this.session.friendlyParty(trainer.id);
    if (party.length === 0) {
      // Their team is resting — keep it warm, just a friendly line then close.
      this.greetMenu?.destroy();
      this.greetMenu = undefined;
      this.greetDialog?.show(
        [`${trainer.username}: maybe another time — my team is resting!`],
        trainer.username,
      );
      this.time.delayedCall(this.reduced ? 600 : 1800, () => this.closeGreeting());
      return;
    }
    const trainerName = trainer.username;
    this.closeGreeting();
    this.busy = true;
    void this.session.save();
    goTo(this, 'Battle', {
      data: {
        kind: 'friendly',
        opponentParty: party,
        opponentName: trainerName,
        returnScene: 'Town',
      },
    });
  }

  /** Step into a real trade with a regular (great for the dex). */
  private openTownTrade(trainer: SimTrainer): void {
    const trainerId = trainer.id;
    this.closeGreeting();
    this.busy = true;
    void this.session.save();
    goTo(this, 'Trade', { data: { trainerId } });
  }

  // ── the living chat hearth ────────────────────────────────────────────────────

  private buildChatPanel(): void {
    this.chatPanel = new Panel(this, 0, 0, { width: 320, height: 240, shadow: true });
    this.chatTitle = this.add.text(0, 0, 'Hearth chat', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.accent,
      fontStyle: 'bold',
    });
    this.chatList = new ScrollList(this, 0, 0, { width: 300, height: 150 });

    // A few warm quick-replies so a tired player can dip in with one tap.
    const quicks = ['Hi everyone!', 'Nice catch!', 'Anyone trading?'];
    for (const text of quicks) {
      const b = new Button(this, 0, 0, {
        label: text,
        variant: 'ghost',
        fontSize: FONT.size.xs,
        height: 40,
        reducedMotion: this.reduced,
        onClick: () => this.sayInChat(text),
      });
      this.quickReplies.push(b);
    }

    // A "show off" quick reply: peacock the lead so the town admires it + asks about
    // it (the two-way heart of D14). Warm — never a comparison, never pressure. The
    // species name lives on the player's echoed line, so the button stays compact.
    if (canShowOff(this.session.follower, this.session.getTeam())) {
      const showOff = new Button(this, 0, 0, {
        label: 'Show off',
        variant: 'warm',
        fontSize: FONT.size.xs,
        height: 40,
        reducedMotion: this.reduced,
        onClick: () => this.showOffLead(),
      });
      this.quickReplies.push(showOff);
    }

    this.chatInput = new TextInput(this, 0, 0, {
      width: 300,
      placeholder: 'say something…',
      maxLength: 120,
      onSubmit: (v) => {
        const t = v.trim();
        if (t) this.sayInChat(t);
        this.chatInput?.setValue('');
      },
    });
  }

  /** Kick off the buffered chat pump: fill the look-ahead buffer, then stream lines. */
  private async startChat(): Promise<void> {
    const recentDeeds = [`${this.session.trainerName} just arrived in town`];
    try {
      await this.session.fillChat(TOWN_CHANNEL, TOWN_NODE, 3, recentDeeds);
    } catch {
      // fillChat is non-throwing by contract; belt-and-braces.
    }
    if (!this.scene.isActive()) return;
    this.pumpChat();
  }

  /** Pull the next ready exchange and stream its lines out at a readable pace. */
  private pumpChat(): void {
    if (this.chatStreaming || !this.scene.isActive()) return;
    const exchange = this.session.getChatExchange(TOWN_CHANNEL);
    if (!exchange || exchange.lines.length === 0) {
      // Buffer empty: top it up during this idle moment, then try again gently.
      void this.refillThenPump();
      return;
    }
    this.streamExchange(exchange.lines);
  }

  private async refillThenPump(): Promise<void> {
    if (!this.scene.isActive()) return;
    try {
      await this.session.fillChat(TOWN_CHANNEL, TOWN_NODE, 3);
    } catch {
      /* non-throwing */
    }
    if (!this.scene.isActive()) return;
    const exchange = this.session.getChatExchange(TOWN_CHANNEL);
    if (exchange && exchange.lines.length > 0) {
      this.streamExchange(exchange.lines);
    } else {
      // Still nothing to say — check back in a little while (calm, never busy-loops).
      this.chatTimer = this.time.delayedCall(4000, () => this.pumpChat());
    }
  }

  /** Stream an exchange's lines into the scroll list one at a time, then continue. */
  private streamExchange(lines: readonly VoiceLine[]): void {
    this.chatStreaming = true;
    let i = 0;
    const showNext = (): void => {
      if (!this.scene.isActive()) return;
      if (i >= lines.length) {
        this.chatStreaming = false;
        // Brief calm pause between exchanges, then keep the hearth flowing.
        this.chatTimer = this.time.delayedCall(2200, () => this.pumpChat());
        return;
      }
      this.appendChatLine(lines[i]);
      i++;
      this.chatTimer = this.time.delayedCall(this.reduced ? 200 : 1400, showNext);
    };
    showNext();
  }

  /** The player's own line (a quick-reply or typed). Echoed immediately + warmly. */
  private sayInChat(text: string): void {
    this.appendChatLine(
      { trainerId: 'player', emotion: 'happy', text },
      this.session.trainerName,
      true,
    );
    // The player addressed the room: one or two nearby residents amble over to answer,
    // so the reply reads as someone crossing the floor to talk (the two-way read, D14).
    this.nominateApproachers();
    // Feed it to the director as a PLAYER beat so the town answers in-character
    // (two-way, D14) — then keep the hearth flowing if it had gone quiet.
    void this.session.sayInChat(text, TOWN_CHANNEL, TOWN_NODE).then(() => this.nudgePump());
  }

  /**
   * Peacock the lead Pokémon: post a show-off beat so nearby regulars admire it and
   * ask follow-up questions about it. Echoes a warm player line, then lets the town
   * react. Pure delight — no comparison, no pressure (D14).
   */
  private showOffLead(): void {
    const lead = showOffTarget(this.session.follower, this.session.getTeam());
    if (!lead) return;
    this.appendChatLine(
      {
        trainerId: 'player',
        emotion: 'joyous',
        text: showOffEchoLine(lead, (id) => this.speciesLabel(id)),
      },
      this.session.trainerName,
      true,
    );
    // Showing off draws admirers over to ooh and ahh at the lead in person.
    this.nominateApproachers();
    void this.session.showOffInChat(TOWN_CHANNEL, TOWN_NODE, lead).then(() => this.nudgePump());
  }

  /** If the chat pump has gone idle, restart it so a queued reply streams out soon. */
  private nudgePump(): void {
    if (!this.scene.isActive() || this.chatStreaming) return;
    this.pumpChat();
  }

  /** Append one chat line (speaker name + body) as a soft row in the scroll list. */
  private appendChatLine(line: VoiceLine, speakerName?: string, isPlayer = false): void {
    if (!this.chatList) return;
    const name = speakerName ?? this.speakerName(line.trainerId);
    const row = this.add.container(0, 0);

    const who = this.add.text(-this.chatLineWidth / 2 + SPACING.sm, 0, name, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.xs}px`,
      color: isPlayer ? CSS.accent : CSS.warm,
      fontStyle: 'bold',
    });
    const body = this.add.text(-this.chatLineWidth / 2 + SPACING.sm, who.height + 1, line.text, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.text,
      wordWrap: { width: this.chatLineWidth - SPACING.md },
      lineSpacing: 2,
    });
    row.add([who, body]);
    const rowHeight = who.height + 1 + body.height + SPACING.sm;

    row.y = this.chatCursorY + rowHeight / 2;
    this.chatList.addItem(row, rowHeight);
    this.chatCursorY += rowHeight;
    this.chatList.scrollToBottom();

    // ALSO pop an overhead bubble over the speaker if they're a walker IN the tavern —
    // the living-hearth read (chat line + a bubble over the head). The player's own
    // lines bubble over the player; a resident's over that resident (if visible here).
    this.popBubbleFor(line, isPlayer);

    // A present resident who pipes up wanders over to say it to the player's face — so
    // NPCs visibly START conversations with you, not just from across the room (D14).
    // Capped + nearby-gated (nominateApproachers), so it's a warm hello, never a swarm.
    if (!isPlayer && line.trainerId !== 'player') {
      const speaker = this.walkerFor(line.trainerId);
      if (speaker && speaker.container.active && !speaker.approaching) {
        this.nominateApproachers([line.trainerId]);
      }
    }
  }

  /**
   * If the speaker of `line` is currently present in the tavern (a walker), pop a
   * transient overhead SpeechBubble over their head — in ADDITION to the chat-panel row.
   * The player's own lines (show-off / replies) bubble over the player. Limited to a few
   * concurrent bubbles (the oldest retires) so the room stays cozy, not spammy; bubbles
   * for off-screen/aggregate speakers simply don't appear (the chat row still does).
   */
  private popBubbleFor(line: VoiceLine, isPlayer: boolean): void {
    const speaker =
      isPlayer || line.trainerId === 'player' ? this.player : this.walkerFor(line.trainerId);
    if (!speaker || !speaker.container.active) return;

    // One bubble per speaker at a time: a fresh line replaces their previous bubble.
    const existing = this.bubbles.find((b) => b.walker === speaker);
    if (existing) {
      existing.bubble.dismiss();
      this.bubbles = this.bubbles.filter((b) => b !== existing);
    }

    // Cozy cap: retire the oldest if we're at the limit.
    while (this.bubbles.length >= MAX_BUBBLES) {
      const oldest = this.bubbles.shift();
      oldest?.bubble.dismiss();
    }

    const bubble = new SpeechBubble(this, 0, 0, {
      text: line.text,
      maxWidth: Math.min(220, Math.max(140, this.groundRect.width * 0.5)),
      reducedMotion: this.reduced,
      accent: isPlayer || line.trainerId === 'player' ? COLORS.warm : COLORS.accent,
    });
    // Float above walkers/scenery but BELOW the greeting + welcome-back overlays (200+),
    // so a modal conversation always reads cleanly over the room.
    bubble.setDepth(120);
    const entry = { walker: speaker, bubble };
    this.bubbles.push(entry);
    this.positionBubble(entry);
    // Drop it from our list once it fades out on its own (dwell timeout).
    bubble.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.bubbles = this.bubbles.filter((b) => b !== entry);
    });
  }

  /** Find the visible walker embodying a society trainer id (or undefined if absent). */
  private walkerFor(trainerId: string): Walker | undefined {
    return this.walkers.find((w) => w.trainer?.id === trainerId);
  }

  /** Pin a bubble just above its walker's head (feet-pos minus the sprite height). */
  private positionBubble(entry: { walker: Walker; bubble: SpeechBubble }): void {
    const c = entry.walker.container;
    // The walker is the player, a labeled regular, or a crowd member — size the lift to
    // its LIVE sprite footprint (phone-shrunk or desktop) so the tail sits just over the
    // head at either size. footprintSide is kept current by syncSpriteScale.
    const side =
      entry.walker === this.player
        ? this.playerSide
        : (entry.walker.footprintSide ??
          (entry.walker.label ? this.residentLabeledSide : this.residentCrowdSide));
    entry.bubble.follow(c.x, c.y - side - BUBBLE_LIFT);
  }

  private speakerName(trainerId: string): string {
    if (trainerId === 'player') return this.session.trainerName;
    const t = this.session.society.find((s) => s.id === trainerId);
    return t?.username ?? 'someone';
  }

  // ── welcome-back letters (letters, not a dashboard) ──────────────────────────

  private showWelcomeBack(welcome: WelcomeBack): void {
    const safe = this.layout.safe;
    const w = Math.min(420, safe.width - SPACING.lg * 2);
    const h = Math.min(safe.height * 0.66, 420);
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    // Modal band depths (above the top-bar greeting at GREETING_DEPTH) so the bright
    // "Welcome back, NAME." banner never bleeds over the letters in portrait.
    const scrim = this.add
      .rectangle(cx, cy, this.layout.width, this.layout.height, COLORS.scrim, 0.55)
      .setOrigin(0.5)
      .setDepth(MODAL_DEPTH)
      .setInteractive();

    const panel = new Panel(this, cx, cy, { width: w, height: h, shadow: true });
    panel.setDepth(MODAL_DEPTH + 1);
    const heading = this.add
      .text(
        cx,
        cy - h / 2 + SPACING.lg,
        welcome.softCapped ? 'While you were away…' : 'A few things happened…',
        {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.lg}px`,
          color: CSS.text,
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5)
      .setDepth(MODAL_DEPTH + 2);

    const listW = w - SPACING.lg * 2;
    const listH = h - SPACING.xxl * 2 - SPACING.md;
    const list = new ScrollList(this, cx, cy - SPACING.sm, { width: listW, height: listH });
    list.setDepth(MODAL_DEPTH + 2);
    // ScrollList maps content-local y=0 to the viewport TOP, so lay letters out from the
    // top down (NOT from -listH/2) — otherwise the first letters clip above the viewport.
    let cursorY = SPACING.sm;
    for (const wl of welcome.letters) {
      const card = new Letter(this, 0, 0, {
        title: humanizeBeatKind(wl.beat.kind),
        body: wl.line.text,
        width: listW - SPACING.md,
      });
      card.y = cursorY + card.cardHeight / 2;
      list.addItem(card, card.cardHeight + SPACING.md);
      cursorY += card.cardHeight + SPACING.md;
    }
    list.setContentHeight(cursorY);
    list.scrollTo(0);

    const dismiss = new Button(this, cx, cy + h / 2 - SPACING.lg, {
      label: 'Settle in',
      variant: 'primary',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => {
        scrim.destroy();
        panel.destroy();
        heading.destroy();
        list.destroy();
        dismiss.destroy();
      },
    });
    dismiss.setDepth(MODAL_DEPTH + 3);
  }

  // ── per-frame wander ──────────────────────────────────────────────────────────

  private onUpdate(_time: number, delta: number): void {
    const dt = Math.min(delta / 1000, 0.1); // clamp a long stall so steps stay sane
    // While free-aiming, the arrow keys nudge the RETICLE (not the player); the player +
    // followers freeze so the cast frame stays steady. The reticle nudge runs first so
    // Enter (handled on its keydown) always fires at the latest position.
    if (this.aimMove) {
      this.stepReticleNudge(dt);
      this.depthSortActors();
      this.positionLabels();
      this.resolveLabelOverlaps();
      for (const entry of this.bubbles) this.positionBubble(entry);
      this.positionCastHint();
      return;
    }
    // Residents wander deliberately; the player walks (keys/tap); followers trail. Do
    // leaders before followers so a follower reads the leader's fresh breadcrumb.
    for (const w of this.walkers) {
      if (w === this.player || w.leader) continue;
      this.stepResident(w, dt);
    }
    if (this.player) this.stepPlayer(this.player, dt);
    for (const w of this.walkers) {
      if (w.leader) this.stepFollowerWalker(w, dt);
    }
    // Now the player's position is fresh: check the walkable exit seam (walk into the
    // bottom band → out to the meadow), debounced so arrival never re-triggers it.
    this.updateSeam();
    // Y-SORT every overworld actor now that all positions are fresh: whoever stands lower
    // on screen draws in front, and a trainer always wins over its own follower.
    this.depthSortActors();
    // Ride each scene-level name tag over its walker's head BEFORE resolving overlaps, so
    // the overlap test reads fresh world positions.
    this.positionLabels();
    this.resolveLabelOverlaps();
    // Keep each live bubble floating over its (moving) speaker's head.
    for (const entry of this.bubbles) this.positionBubble(entry);
    // Ride the "✦ Cast" hint over the follower's head; hide it while the cast menu/effect
    // is up (the scrim covers the room then) or a chat bubble is over the follower.
    this.positionCastHint();
  }

  /** Float the "✦ Cast" prompt just over the follower's head (or hide it mid-overlay). */
  private positionCastHint(): void {
    const hint = this.castHint;
    const f = this.follower;
    if (!hint) return;
    if (!f || !f.container.active || this.castMenu || this.casting || this.aimMove) {
      hint.setVisible(false);
      return;
    }
    hint.setVisible(true);
    // The follower chibi is small + zoomed; lift the hint a little above the container.
    hint.setPosition(f.container.x, f.container.y - 64);
  }

  /**
   * Y-SORT all overworld actors so the scene reads with depth: the actor LOWER on screen
   * (larger container y) draws in front of one higher up — the classic overworld look where
   * you pass in front of / behind other trainers. We RANK-order the live actors by y (with a
   * follower tied just behind its own leader) and assign sequential depths from
   * DEPTH_SORT_BASE upward, so the whole sprite band stays under the labels/bubbles/modals
   * regardless of raw screen-y. Fixes the "follower piled on top of the trainer" read:
   * trailing below its leader, the partner's larger y would naively draw it in front, but
   * the leader-tie rule keeps the trainer cleanly in front of its own follower. Cheap: one
   * sort + one setDepth per walker per frame, the same actors the wander loop already touches.
   */
  private depthSortActors(): void {
    const live = this.walkers.filter((w) => w.container.active);
    const order = actorDepthOrder(
      live.map((w, i) => ({
        index: i,
        y: w.container.y,
        isFollower: w.leader != null,
        leaderIndex: w.leader ? live.indexOf(w.leader) : -1,
      })),
    );
    for (let depth = 0; depth < order.length; depth++) {
      live[order[depth]!]!.container.setDepth(DEPTH_SORT_BASE + depth);
    }
  }

  /**
   * Pin every name tag just above its walker's head each frame. The labels are SCENE-level
   * objects (not container children), so they're positioned in WORLD space here — this is
   * what keeps each tag reliably above its (moving) resident + the player, and above any
   * other sprite, every frame. The lift is derived from the walker's footprint so the bump
   * to bigger sprites keeps the tag just over the head.
   */
  private positionLabels(): void {
    for (const w of this.walkers) {
      const side =
        w === this.player ? this.playerSide : (w.footprintSide ?? this.residentLabeledSide);
      const labelY = w.container.y - side - NAME_LABEL_GAP;
      if (w.label) w.label.setPosition(w.container.x, labelY);
      if (w.questMarker) {
        // Ride just above the name tag (or the head, if unlabeled), a soft "here!" beacon.
        const above = (w.label?.height ?? FONT.size.xs) + NAME_LABEL_GAP;
        w.questMarker.setPosition(w.container.x, labelY - above);
      }
    }
  }

  /**
   * Light anti-overlap for the floating name tags. The labels are SCENE-level now, so
   * their x/y are already WORLD coords (positionLabels just set them). Two residents whose
   * heads drift close would make their names graze into a smudge, so each frame we fade the
   * lower-priority label of any colliding pair — the player's own name always wins, and
   * among NPCs the earlier-spawned (more "anchored") one stays lit.
   *
   * Two safeguards so a tag is NEVER left stuck hidden: (1) every label is FORCE-shown
   * (visible + full alpha) at the top of the pass before any dimming, so last frame's dim
   * always heals; (2) the dim only ever drops alpha to NAME_DIM_ALPHA (a faint ghost, not
   * 0) and never touches `.visible`, so a label that stops colliding pops right back to
   * full. Only the small labeled cast is checked, so this stays cheap.
   */
  private resolveLabelOverlaps(): void {
    // Reset EVERY label (not just the currently-visible ones) so nothing can get stuck.
    const labeled = this.walkers.filter((w) => w.label);
    for (const w of labeled) {
      w.label!.setVisible(true);
      w.label!.setAlpha(1);
    }
    for (let i = 0; i < labeled.length; i++) {
      for (let j = i + 1; j < labeled.length; j++) {
        const a = labeled[i]!;
        const b = labeled[j]!;
        const ax = a.label!.x;
        const ay = a.label!.y;
        const bx = b.label!.x;
        const by = b.label!.y;
        if (Math.abs(ax - bx) < NAME_COLLIDE_X && Math.abs(ay - by) < NAME_COLLIDE_Y) {
          // The player's tag (trainer === null) never yields; otherwise b (the later
          // walker in the list) fades behind a.
          const yielder = a.trainer === null ? b : b.trainer === null ? a : b;
          yielder.label!.setAlpha(NAME_DIM_ALPHA);
        }
      }
    }
    // The persistent welcome-back header is UI — fade any name tag that drifts up into it
    // (but never the player's own tag, which always stays legible).
    const header = this.greeting;
    if (header && header.visible) {
      const hb = header.getBounds();
      for (const w of labeled) {
        if (
          w !== this.player &&
          Phaser.Geom.Intersects.RectangleToRectangle(hb, w.label!.getBounds())
        ) {
          w.label!.setAlpha(NAME_DIM_ALPHA);
        }
      }
    }
  }

  /** Wander bounds (the town ground, with a margin keeping feet inside the rect). */
  private get wanderBounds(): WanderBounds {
    const g = this.groundRect;
    return { x: g.x, y: g.y, width: g.width, height: g.height, margin: 32 };
  }

  /**
   * Deliberate cardinal stop-and-go wander for an NPC resident (pure stepWander state).
   * Walk a tile or two in one direction, stop and idle a beat, sometimes turn, repeat —
   * the classic Pokemon-town read, never a floaty diagonal drift.
   */
  private stepResident(w: Walker, dt: number): void {
    if (!w.wander) return;
    // A resident "walking over to talk" heads cardinally toward the player instead of
    // wandering, then parks + faces them for a beat while the chat + bubble play.
    if (w.approaching) {
      this.stepApproacher(w, dt);
      return;
    }
    const r = stepWander(w.wander, dt, this.wanderBounds);
    w.container.setPosition(w.wander.x, w.wander.y);
    this.recordTrail(w);
    if (r.moving) this.driveWalkAnim(w, w.wander.facing);
    else this.settleWalker(w);
  }

  /**
   * Drive a resident who is walking over to talk to the player: amble in CARDINAL
   * steps toward its assigned STANDOFF point (a distinct slot on a ring around the
   * player, so several approachers gather AROUND rather than ON the player), and once
   * there stop + turn to FACE the player and linger until `approachUntil`. When the
   * dwell ends, release it back to its deliberate wander from wherever it stands.
   */
  private stepApproacher(w: Walker, dt: number): void {
    if (!this.player) {
      this.releaseApproacher(w);
      return;
    }
    const now = this.time.now;
    const px = this.player.container.x;
    const py = this.player.container.y;
    // The target is the assigned ring slot; fall back to the player's spot if (defensively)
    // none was assigned. Facing is always toward the PLAYER — they came over to talk to you.
    const tx = w.approachX ?? px;
    const ty = w.approachY ?? py;
    const gx = tx - w.container.x;
    const gy = ty - w.container.y;
    const goalDist = Math.hypot(gx, gy);
    const fdx = px - w.container.x;
    const fdy = py - w.container.y;
    const facePlayer: Cardinal =
      Math.abs(fdx) >= Math.abs(fdy) ? (fdx >= 0 ? 'east' : 'west') : fdy >= 0 ? 'south' : 'north';

    // Arrived at (or close enough to) the standoff slot: park, face the player, dwell.
    if (w.approachArrived || goalDist <= APPROACH_ARRIVE_EPS) {
      if (!w.approachArrived) {
        w.approachArrived = true;
        w.approachUntil = now + APPROACH_DWELL_MS;
      }
      this.faceWalker(w, facePlayer);
      this.settleWalker(w);
      if (w.approachUntil != null && now >= w.approachUntil) this.releaseApproacher(w);
      return;
    }

    // Still en route: take a steady cardinal step toward the STANDOFF slot (dominant axis).
    const dir: Cardinal =
      Math.abs(gx) >= Math.abs(gy) ? (gx >= 0 ? 'east' : 'west') : gy >= 0 ? 'south' : 'north';
    const g = this.groundRect;
    const m = 28;
    let nx = w.container.x;
    let ny = w.container.y;
    if (dir === 'east' || dir === 'west') nx += Math.sign(gx) * APPROACH_SPEED * dt;
    else ny += Math.sign(gy) * APPROACH_SPEED * dt;
    // Don't overshoot the slot on the moving axis (so it settles ON the point, not past it).
    if (dir === 'east' || dir === 'west') nx = gx > 0 ? Math.min(nx, tx) : Math.max(nx, tx);
    else ny = gy > 0 ? Math.min(ny, ty) : Math.max(ny, ty);
    nx = Phaser.Math.Clamp(nx, g.x + m, g.x + g.width - m);
    ny = Phaser.Math.Clamp(ny, g.y + m, g.y + g.height - m);
    w.container.setPosition(nx, ny);
    // Keep the wander state in sync so the resident resumes from HERE, not a stale goal.
    if (w.wander) {
      w.wander.x = nx;
      w.wander.y = ny;
    }
    this.recordTrail(w);
    this.driveWalkAnim(w, dir);
  }

  /** Turn a walker to face a direction without re-triggering the walk-bob. */
  private faceWalker(w: Walker, dir: Cardinal): void {
    w.facing = dir;
    if (w.avatar && (dir === 'east' || dir === 'west')) w.avatar.sprite.setFlipX(dir === 'west');
    w.actor?.face(cardinalToDirection(dir));
  }

  /** Clear a resident's approach state + resume its deliberate wander from where it is. */
  private releaseApproacher(w: Walker): void {
    w.approaching = false;
    w.approachArrived = false;
    w.approachUntil = undefined;
    w.approachX = undefined;
    w.approachY = undefined;
    this.approachers.delete(w);
    if (w.wander) {
      // Re-anchor the wander goal to the current spot so it doesn't snap back.
      w.wander.x = w.container.x;
      w.wander.y = w.container.y;
      w.wander.goalX = w.container.x;
      w.wander.goalY = w.container.y;
    }
  }

  /**
   * Nominate up to {@link MAX_APPROACHERS} NEARBY resident walkers (embodying the given
   * society trainer ids when provided, else any nearby resident) to walk over and greet
   * the player. The cozy two-way read: when the player addresses the room or a resident
   * pipes up, someone actually crosses the floor to talk. Idempotent + capped — already
   * approaching residents keep their turn; a busy moment never summons a crowd.
   */
  private nominateApproachers(
    preferTrainerIds: readonly string[] = [],
    max = MAX_APPROACHERS,
  ): void {
    if (!this.player) return;
    const slots = max - this.approachers.size;
    if (slots <= 0) return;

    const px = this.player.container.x;
    const py = this.player.container.y;
    const nearby = (w: Walker): boolean =>
      Math.hypot(w.container.x - px, w.container.y - py) <= APPROACH_NEARBY_RADIUS;

    // Candidates: real resident walkers (have a trainer + wander), present + nearby, not
    // already approaching. Prefer the named speakers, then the closest others.
    const eligible = this.walkers.filter(
      (w) => w.trainer && w.wander && !w.approaching && w.container.active && nearby(w),
    );
    const preferred = eligible.filter((w) => preferTrainerIds.includes(w.trainer!.id));
    const rest = eligible
      .filter((w) => !preferTrainerIds.includes(w.trainer!.id))
      .sort(
        (a, b) =>
          Math.hypot(a.container.x - px, a.container.y - py) -
          Math.hypot(b.container.x - px, b.container.y - py),
      );

    for (const w of [...preferred, ...rest].slice(0, slots)) {
      w.approaching = true;
      w.approachArrived = false;
      w.approachUntil = undefined;
      // Give this resident its own STANDOFF slot on the ring around the player, spaced
      // clear of anyone already approaching — so they gather AROUND the player, not ON it.
      const slot = this.standoffPoint(w, px, py);
      w.approachX = slot.x;
      w.approachY = slot.y;
      this.approachers.add(w);
    }
  }

  /**
   * Pick a STANDOFF point (feet coords) at {@link APPROACH_DISTANCE} around the player for
   * a newly-nominated approacher `w`. The chosen angle sits at least {@link APPROACH_SLOT_SEP}
   * clear of every already-approaching resident's slot, biased toward `w`'s own natural
   * bearing from the player (so it walks the sensible short way in). Clamped to the walkable
   * ground so a slot never lands off-floor. This is what turns the old on-top pile into a
   * warm little arc gathering beside the player.
   */
  private standoffPoint(w: Walker, px: number, py: number): { x: number; y: number } {
    // Angles already claimed by the residents mid-approach (their assigned slots).
    const taken: number[] = [];
    for (const other of this.approachers) {
      if (other === w || other.approachX == null || other.approachY == null) continue;
      taken.push(Math.atan2(other.approachY - py, other.approachX - px));
    }
    const angDist = (a: number, b: number): number => {
      const d = Math.abs(a - b) % (Math.PI * 2);
      return d > Math.PI ? Math.PI * 2 - d : d;
    };
    const clears = (cand: number): boolean =>
      taken.every((t) => angDist(cand, t) >= APPROACH_SLOT_SEP);

    // Natural bearing = the direction the resident already stands relative to the player.
    const natural = Math.atan2(w.container.y - py, w.container.x - px);
    let angle = natural;
    if (!clears(natural)) {
      // Fan out from the natural bearing in growing ± steps; take the first clear slot.
      const step = Math.PI / 12; // 15°
      let found = false;
      for (let off = step; off <= Math.PI + 1e-6 && !found; off += step) {
        for (const cand of [natural + off, natural - off]) {
          if (clears(cand)) {
            angle = cand;
            found = true;
            break;
          }
        }
      }
      if (!found) angle = natural + Math.PI; // fully ringed: settle opposite (defensive)
    }

    const g = this.groundRect;
    const m = 28;
    const x = Phaser.Math.Clamp(
      px + Math.cos(angle) * APPROACH_DISTANCE,
      g.x + m,
      g.x + g.width - m,
    );
    const y = Phaser.Math.Clamp(
      py + Math.sin(angle) * APPROACH_DISTANCE,
      g.y + m,
      g.y + g.height - m,
    );
    return { x, y };
  }

  /**
   * The player walks at a steady pace in CARDINAL directions: arrow keys / WASD when
   * held (priority), else toward the last tapped destination. No diagonal gliding — the
   * dominant axis wins, exactly like a classic overworld.
   */
  private stepPlayer(w: Walker, dt: number): void {
    const key = this.keyboardVector();
    let dx = 0;
    let dy = 0;
    if (key) {
      // Cardinal: take the dominant axis only (no diagonal).
      if (Math.abs(key.x) >= Math.abs(key.y) && key.x !== 0) dx = Math.sign(key.x);
      else dy = Math.sign(key.y);
      // Keying cancels a pending tap-walk so the two never fight.
      w.targetX = w.container.x;
      w.targetY = w.container.y;
    } else {
      const tdx = w.targetX - w.container.x;
      const tdy = w.targetY - w.container.y;
      if (Math.hypot(tdx, tdy) >= 2) {
        if (Math.abs(tdx) >= Math.abs(tdy)) dx = Math.sign(tdx);
        else dy = Math.sign(tdy);
      }
    }

    if (dx === 0 && dy === 0) {
      this.settleWalker(w);
      return;
    }

    const g = this.groundRect;
    const m = 28;
    let nx = w.container.x + dx * w.speed * dt;
    let ny = w.container.y + dy * w.speed * dt;
    // Don't overshoot a tap target on the moving axis.
    if (!key) {
      if (dx !== 0) nx = dx > 0 ? Math.min(nx, w.targetX) : Math.max(nx, w.targetX);
      if (dy !== 0) ny = dy > 0 ? Math.min(ny, w.targetY) : Math.max(ny, w.targetY);
    }
    nx = Phaser.Math.Clamp(nx, g.x + m, g.x + g.width - m);
    ny = Phaser.Math.Clamp(ny, g.y + m, g.y + g.height - m);
    w.container.setPosition(nx, ny);
    this.recordTrail(w);
    const dir: Cardinal = dx !== 0 ? (dx > 0 ? 'east' : 'west') : dy > 0 ? 'south' : 'north';
    this.driveWalkAnim(w, dir);
  }

  /** Record one breadcrumb of a leader's position (for its trailing follower). */
  private recordTrail(leader: Walker): void {
    const trail = this.leaderTrail(leader);
    const last = trail[trail.length - 1];
    if (
      !last ||
      Math.abs(last.x - leader.container.x) > 1 ||
      Math.abs(last.y - leader.container.y) > 1
    ) {
      trail.push({ x: leader.container.x, y: leader.container.y });
      if (trail.length > 48) trail.shift();
    }
  }

  private leaderTrail(leader: Walker): { x: number; y: number }[] {
    let trail = this.trails.get(leader);
    if (!trail) {
      trail = [{ x: leader.container.x, y: leader.container.y }];
      this.trails.set(leader, trail);
    }
    return trail;
  }

  /**
   * A follower trails its leader a fixed gap behind, walking the SAME deliberate
   * cardinal path (stepFollow moves one axis at a time — no floaty diagonal catch-up).
   * It consumes the leader's breadcrumb trail so it follows the route, not a straight
   * line through scenery.
   */
  private stepFollowerWalker(f: Walker, dt: number): void {
    const leader = f.leader;
    if (!leader) return;
    const trail = this.leaderTrail(leader);
    const gap = f.followGap ?? 30;
    // Aim a fixed arc-length behind the leader along its breadcrumb trail, then nudge the
    // rest point a touch DOWN-screen so a stopped leader's collapsed trail (a single point
    // at the leader) doesn't stack the follower exactly on top of the trainer — it settles
    // just below the leader's feet instead, and depthSortActors keeps the trainer in front.
    const behind = trailPointBehind(trail, gap) ?? {
      x: leader.container.x,
      y: leader.container.y,
    };
    const target = { x: behind.x, y: behind.y + FOLLOW_BELOW };
    const res = stepFollow({ x: f.container.x, y: f.container.y }, target, f.speed, dt, 1.5);
    f.container.setPosition(res.x, res.y);
    if (res.moving && res.facing) this.driveWalkAnim(f, res.facing);
    else this.settleWalker(f);
  }

  /** Drive a walker's motion cue: the trainer walk-bob (+ flip), or the PMD chibi anim. */
  private driveWalkAnim(w: Walker, dir: Cardinal): void {
    w.facing = dir;
    if (!w.moving) {
      w.moving = true;
      w.avatar?.playWalk(this.reduced);
    }
    // Static trainer fronts get a soft horizontal flip as a left/right facing cue.
    if (w.avatar && (dir === 'east' || dir === 'west')) w.avatar.sprite.setFlipX(dir === 'west');
    w.actor?.walk(cardinalToDirection(dir));
  }

  /** Settle a walker to a calm standing idle facing its last direction. */
  private settleWalker(w: Walker): void {
    if (w.moving) {
      w.moving = false;
      w.avatar?.stopWalk();
    }
    w.actor?.idle();
  }

  private randomGroundPoint(): { x: number; y: number } {
    const g = this.groundRect;
    const pad = 40;
    return {
      x: g.x + pad + Math.random() * Math.max(1, g.width - pad * 2),
      y: g.y + pad + Math.random() * Math.max(1, g.height - pad * 2),
    };
  }

  // ── responsive layout ─────────────────────────────────────────────────────────

  /**
   * True on a narrow/portrait PHONE — the case the desktop +50% sprites overcrowd. We
   * treat the layout as "phone" when it is compact (< 820) AND genuinely small in at
   * least one dimension (a short ~720 height or a narrow ~520 width). This keeps the
   * big sprites on a roomy tablet/desktop but shrinks + thins them on an actual phone.
   */
  private get phoneMode(): boolean {
    const safe = this.layout.safe;
    return this.layout.isCompact && (safe.height < 720 || safe.width < 520);
  }

  /**
   * Recompute the live sprite footprints for the current viewport (full on desktop,
   * ×PHONE_SPRITE_SCALE on a phone) and re-fit the already-spawned avatars, hit-areas
   * and per-walker footprintSide so a rotation/resize rescales the whole room. The
   * name-label + speech-bubble lifts read these fields each frame, so they follow too.
   */
  private syncSpriteScale(): void {
    const f = this.phoneMode ? PHONE_SPRITE_SCALE : 1;
    this.residentLabeledSide = Math.round(RESIDENT_LABELED_SIDE * f);
    this.residentCrowdSide = Math.round(RESIDENT_CROWD_SIDE * f);
    this.playerSide = Math.round(PLAYER_SIDE * f);

    for (const w of this.walkers) {
      if (w.leader) continue; // followers are sized leader-relative (re-dressed below)
      const isPlayer = w === this.player;
      const side = isPlayer
        ? this.playerSide
        : w.label
          ? this.residentLabeledSide
          : this.residentCrowdSide;
      w.footprintSide = side;
      if (w.avatar) this.fitTrainer(w.avatar, side);
      // Grow/shrink the tap hit-area with the sprite so a thumb still lands cleanly.
      // The player's container is non-interactive (only residents are tappable), so
      // only residents have an `input` to resize.
      const hitH = side + 8;
      w.container.setSize(side, hitH);
      if (w.container.input) {
        w.container.input.hitArea = new Phaser.Geom.Rectangle(-side / 2, -hitH, side, hitH);
      }
      // Re-size this leader's follower against its NEW footprint (keeps the 1.2× cap).
      const follower = this.walkers.find((c) => c.leader === w);
      if (follower?.actor) this.refitFollower(follower, side);
    }
  }

  /** Re-fit an already-dressed follower's chibi against its leader's new footprint. */
  private refitFollower(follower: Walker, leaderSide: number): void {
    const actor = follower.actor;
    if (!actor) return;
    const height = follower.dexHeight;
    const sizeFactor =
      typeof height === 'number' && height > 0 ? followerScale(height) : FOLLOWER_SCALE_DEFAULT;
    fitOverworldActor(
      actor,
      FOLLOWER_FIT_BASE * followerScaleCap(leaderSide, sizeFactor),
      SPRITE_SCALE_CAP,
    );
  }

  private relayout(): void {
    const safe = this.layout.safe;
    const compact = this.layout.isCompact;
    // The aim layer/reticle/prompt/Done are sized to the OLD ground rect; a resize (rotation)
    // would strand them. Cast mode is transient + cheap to re-open, so just exit it cleanly.
    if (this.aimMove) this.exitCastAimMode();
    this.syncSpriteScale();

    // Set-out top-right first (its width feeds the greeting's width cap below).
    if (this.setOutButton) {
      const b = this.setOutButton;
      b.setPosition(
        safe.x + safe.width - SPACING.lg - b.buttonWidth / 2,
        safe.y + SPACING.md + b.buttonHeight / 2,
      );
    }
    // Greeting top-left: COMPACT line + smaller font + a width cap that excludes the Set
    // out column on a phone (so it never reaches the button or the crowd's name labels);
    // full lively line on desktop.
    this.syncGreeting(compact);
    this.greeting?.setPosition(safe.x + SPACING.lg, safe.y + SPACING.md);

    const topBarH = SPACING.md + 48 + SPACING.md;

    // Chat: a bottom sheet on a phone, a right rail on a wide screen.
    let groundX: number;
    let groundY: number;
    let groundW: number;
    let groundH: number;

    if (compact) {
      // The chat bottom-sheet was only ~2 lines tall on a phone (0.4 height, 260 cap) so
      // the history couldn't be read. Give it ~half the bottom-sheet height with a higher
      // cap so several lines show AND the touch-drag scroll has real travel — while still
      // leaving the (now phone-shrunk) town a usable strip above. The reserved 56px below
      // keeps the sheet clear of the home-indicator + leaves the menu pills room.
      const reserveBelow = 56;
      const minGroundH = 180; // keep the room walkable even on a short phone
      const idealChatH = Math.min(safe.height * 0.5, 420);
      const maxChatH = safe.height - topBarH - SPACING.lg - reserveBelow - minGroundH;
      const chatH = Math.max(180, Math.min(idealChatH, Math.max(180, maxChatH)));
      groundX = safe.x + SPACING.md;
      groundY = safe.y + topBarH;
      groundW = safe.width - SPACING.md * 2;
      groundH = safe.height - topBarH - chatH - SPACING.lg - reserveBelow;
      this.layoutChat(
        safe.x + safe.width / 2,
        safe.y + safe.height - chatH / 2 - reserveBelow,
        safe.width - SPACING.md * 2,
        chatH,
      );
    } else {
      const chatW = Math.min(360, safe.width * 0.34);
      groundX = safe.x + SPACING.md;
      groundY = safe.y + topBarH;
      groundW = safe.width - chatW - SPACING.lg * 2;
      groundH = safe.height - topBarH - SPACING.md;
      this.layoutChat(
        safe.x + safe.width - chatW / 2 - SPACING.md,
        safe.y + topBarH + (safe.height - topBarH - SPACING.md) / 2,
        chatW,
        safe.height - topBarH - SPACING.md,
      );
    }

    this.groundRect = {
      x: groundX,
      y: groundY,
      width: Math.max(40, groundW),
      height: Math.max(40, groundH),
    };
    this.drawGround();
    this.layoutMenu(compact);
    this.clampWalkersToGround();

    // Seat the on-screen d-pad in the walkable room's bottom-right (clear of the bottom-
    // left menu toggle + the chat sheet below). Shown only on a coarse-pointer (touch)
    // device — a mouse desktop walks by keyboard and hides the pad to keep the room clean.
    if (this.dpad) {
      this.dpad.setVisible(isCoarsePointer());
      this.dpad.layout(this.groundRect, 'bottom-right');
    }

    // Seat the exit signpost at the top-centre of the (recomputed) bottom band.
    this.positionSeam();
  }

  /**
   * Lay out the chat panel + its list + quick-replies + input at (cx, cy).
   *
   * The list is given the space LEFT OVER after the title, the (wrapped) quick-reply
   * block and the input are budgeted from the bottom up — so on a phone the controls
   * always sit fully inside the sheet (never spilling over its border or each other,
   * the earlier overlap), and the scrollable history gets every remaining pixel. On a
   * phone the quick-reply + input rows are a touch shorter to free more room for the
   * history, which (with the taller sheet from relayout) restores readable, scrollable
   * chat. The list rect is the ONLY drag-scroll surface here; the quick-replies + input
   * are laid out strictly below it (non-overlapping), so the scroll gesture is never
   * swallowed by an input handler.
   */
  private layoutChat(cx: number, cy: number, w: number, h: number): void {
    this.chatPanel?.setPosition(cx, cy).resize(w, h);
    const innerW = w - SPACING.md * 2;
    this.chatLineWidth = innerW;

    const phone = this.phoneMode;
    const top = cy - h / 2 + SPACING.md;
    this.chatTitle?.setPosition(cx - w / 2 + SPACING.md, top);

    // These MUST match the actual rendered control heights (TextInput defaults to
    // TOUCH_TARGET=48; the quick-reply Buttons were built at height 40), so the budget
    // below reserves exactly the space the controls occupy and nothing overlaps the
    // panel border or the list above.
    const inputH = 48;
    const quickH = 40;
    const gap = SPACING.xs;
    const n = this.quickReplies.length;

    // Quick-replies wrap to as many columns as fit at a comfortable min width, so the
    // labels never overflow their buttons on a narrow panel (the screenshots' spill).
    const minBtnW = phone ? 84 : 92;
    const perRow = Math.max(1, Math.min(n, Math.floor((innerW + gap) / (minBtnW + gap))));
    const qRows = n > 0 ? Math.ceil(n / perRow) : 0;
    const qBlockH = qRows > 0 ? qRows * quickH + (qRows - 1) * gap : 0;

    // Budget from the top: title band, then the list, then a gap, the quick-reply block,
    // a gap, and the input — all inside `h`. The list gets whatever's left (>= a usable
    // minimum so the history is several lines tall and worth scrolling).
    const titleBandH = SPACING.lg;
    const belowList = (qBlockH > 0 ? SPACING.sm + qBlockH : 0) + SPACING.sm + inputH + SPACING.md;
    const listH = Math.max(64, h - SPACING.md - titleBandH - belowList);

    const listTop = top + titleBandH;
    const listCy = listTop + listH / 2;
    this.chatList?.setPosition(cx, listCy).resize(innerW, listH);

    const listBottom = listCy + listH / 2;

    // Quick-reply grid beneath the list (rows top-down).
    if (qRows > 0) {
      const qW = (innerW - gap * (perRow - 1)) / perRow;
      const qTop = listBottom + SPACING.sm + quickH / 2;
      this.quickReplies.forEach((b, i) => {
        const col = i % perRow;
        const row = Math.floor(i / perRow);
        const rowCount = row === qRows - 1 ? n - row * perRow : perRow;
        const rowW = rowCount * qW + (rowCount - 1) * gap;
        const startX = cx - rowW / 2 + qW / 2;
        b.setWidth(qW);
        b.setPosition(startX + col * (qW + gap), qTop + row * (quickH + gap));
      });
    }

    // Input at the bottom, below the whole quick-reply block.
    const iy = listBottom + (qBlockH > 0 ? SPACING.sm + qBlockH : 0) + SPACING.sm + inputH / 2;
    this.chatInput?.setPosition(cx, iy);

    // Resync list content height after a resize, then pin to the latest line.
    this.chatList?.setContentHeight(this.chatCursorY);
    this.chatList?.scrollToBottom();
  }

  /**
   * Position the menu DRAWER for the current viewport, then the toggle + contents inside
   * it. The drawer rests as a band: on DESKTOP flush to the safe-area bottom (the chat is
   * a right rail, so the bottom strip is free); on a PHONE anchored to the bottom of the
   * walkable GROUND band ABOVE the chat bottom-sheet, so the toggle + drawer live together
   * in the tavern area and the drawer NEVER overlaps the chat input/quick-replies. The
   * drawer overlays everything (high depth) when open; the toggle is drawn above the scrim
   * so "tap again to close" works. Re-applies the live open/closed transform so a rotation
   * never strands a half-open drawer.
   */
  private layoutMenu(compact: boolean): void {
    const safe = this.layout.safe;
    const g = this.groundRect;

    // Size the drawer as a band. On a phone it spans the safe width (chat is a bottom
    // sheet BELOW it). On DESKTOP the chat is a right RAIL, so the drawer must stay within
    // the LEFT (non-chat) column: cap its width to the ground band minus a margin so its
    // right edge never crosses into the chat rail's border (the ~16px overlap the tester
    // saw). Its height fits the heading + the wrapped nav grid, capped for short phones.
    const desktopChatW = Math.min(360, safe.width * 0.34);
    const desktopAvail = safe.width - desktopChatW - SPACING.md * 2 - SPACING.md;
    const drawerW = compact
      ? Math.min(safe.width - SPACING.md * 2, safe.width)
      : Math.min(safe.width - SPACING.md * 2, 560, Math.max(280, desktopAvail));
    const innerW = drawerW - SPACING.lg * 2;
    const n = this.menuButtons.length;
    const bw = this.menuButtons.reduce((m, b) => Math.max(m, b.button.buttonWidth), 0) || 132;
    const gap = SPACING.sm;
    const perRow = Math.max(1, Math.min(n, Math.floor((innerW + gap) / (bw + gap))));
    const rows = n > 0 ? Math.ceil(n / perRow) : 0;
    const rowH = 44 + gap;
    const headingBand = SPACING.lg + SPACING.xs;
    const drawerH = SPACING.lg + headingBand + rows * rowH - gap + SPACING.lg;

    // Where the drawer RESTS depends on the form factor:
    //  • Desktop (chat is a right RAIL): the bottom strip is free, so rest the drawer
    //    flush to the safe-area bottom (above the home indicator) as before.
    //  • Phone (chat is a bottom SHEET occupying the lower ~half): the safe-area bottom
    //    is INSIDE the chat sheet — resting there overlapped the chat input + quick
    //    replies. So anchor the drawer's BOTTOM flush to the bottom of the walkable
    //    GROUND band (which sits a gap ABOVE the chat sheet), letting the toggle + drawer
    //    live together in the tavern area, NEVER over the chat. If a tall menu on a short
    //    phone doesn't fit the band, its TOP floats up into the top strip (it never grows
    //    DOWN past the ground bottom into the chat) — the greeting still draws cleanly over
    //    it (GREETING_DEPTH), so it stays a tidy in-room panel.
    // Centre the drawer in its column: the FULL safe width on a phone (chat is below),
    // but the LEFT (non-chat) ground band on desktop so the narrower drawer sits centred
    // clear of the right-rail chat — never grazing its border.
    const drawerCx = compact ? safe.x + safe.width / 2 : g.x + Math.max(g.width, drawerW) / 2;
    const drawerBottom = compact ? g.y + g.height - SPACING.sm : safe.y + safe.height - SPACING.sm;
    // Anchor the BOTTOM (so a phone drawer is always flush above the chat sheet); the top
    // floats up by the drawer's height. A menu taller than the ground band just rides up
    // into the top strip (where the greeting, at GREETING_DEPTH, still draws cleanly over
    // it) — it never grows DOWN into the chat, which is the overlap we're killing.
    this.drawerRect = {
      x: drawerCx - drawerW / 2,
      y: drawerBottom - drawerH,
      width: drawerW,
      height: drawerH,
    };

    // The compact toggle CHIP. When CLOSED it parks at the bottom-LEFT of the walkable
    // ROOM (the ground rect, which sits ABOVE the chat sheet) so it never covers the
    // centred crowd and never overlaps the chat. When OPEN it sits as a small tab just
    // ABOVE the drawer's top-left edge (and is drawn above the scrim + panel), so "tap the
    // toggle again to close" always lands on the toggle, not the scrim. A fixed
    // TOGGLE_SIDE square keeps it a quiet ≡ / × icon chip, never a wide pill.
    const toggle = this.menuToggle;
    if (toggle) {
      toggle.setWidth(TOGGLE_SIDE);
      this.positionToggle();
    }

    this.menuDrawer?.resize(drawerW, drawerH);

    // Scrim dims the room behind the open drawer; a tap on it (outside the panel) closes
    // the drawer. On a PHONE it covers the WHOLE viewport (the chat is a bottom SHEET the
    // closed drawer sits above, so nothing usable is lost while the drawer is open). On
    // DESKTOP the chat is a right RAIL and the drawer defaults OPEN — a full-viewport scrim
    // sat OVER that rail, so the FIRST click on the chat input merely hit the scrim (closing
    // the drawer) and had to be repeated to focus. So on desktop we shrink the scrim to the
    // LEFT column only, stopping at the chat rail's left edge: the chat stays click-through
    // (first click focuses it naturally) and the open drawer simply stays open.
    if (compact) {
      this.menuScrim
        ?.setPosition(this.layout.centerX, this.layout.centerY)
        .setSize(this.layout.width, this.layout.height);
    } else {
      const chatLeft = safe.x + safe.width - desktopChatW - SPACING.md;
      const scrimW = Math.max(0, chatLeft);
      this.menuScrim
        ?.setPosition(scrimW / 2, this.layout.centerY)
        .setSize(scrimW, this.layout.height);
    }

    // Apply the current open/closed transform without re-animating (a relayout while
    // open keeps it open and correctly placed; while closed keeps it tucked + hidden).
    // A resize mid-slide would fight a relative tween, so cancel any live one + snap to
    // the resolved end-state (full alpha when open, hidden when closed).
    this.menuTween?.remove();
    this.menuTween = undefined;
    if (this.menuOpen) {
      const drawer = this.menuDrawer;
      if (drawer) {
        drawer
          .setAlpha(1)
          .setVisible(true)
          .setPosition(drawerCx, this.drawerRect.y + drawerH / 2);
      }
      this.menuHeading?.setVisible(true).setAlpha(1);
      for (const { button } of this.menuButtons) button.setVisible(true).setAlpha(1);
      this.menuScrim?.setVisible(true).setAlpha(0.45);
      this.layoutMenuContents();
    } else {
      this.menuDrawer?.setVisible(false);
      this.menuScrim?.setVisible(false);
      this.menuHeading?.setVisible(false);
      for (const { button } of this.menuButtons) button.setVisible(false);
    }
  }

  /**
   * Place the drawer's heading + the wrapped grid of nav buttons relative to the
   * (resting) drawer rect. Called whenever the drawer is shown/relaid; the slide
   * animation moves these together with the panel by tweening their shared y-offset.
   */
  private layoutMenuContents(): void {
    const rect = this.drawerRect;
    const innerW = rect.width - SPACING.lg * 2;
    const gap = SPACING.sm;
    const n = this.menuButtons.length;

    // Heading at the top-left of the drawer.
    this.menuHeading?.setPosition(rect.x + SPACING.lg, rect.y + SPACING.lg);

    if (n === 0) return;
    const bw = this.menuButtons.reduce((m, b) => Math.max(m, b.button.buttonWidth), 0) || 132;
    const perRow = Math.max(1, Math.min(n, Math.floor((innerW + gap) / (bw + gap))));
    this.menuNavCols = perRow;
    const rows = Math.ceil(n / perRow);
    const rowH = 44 + gap;
    // Buttons fill the row width evenly so the grid reads tidy (no ragged right edge).
    const cellW = (innerW - gap * (perRow - 1)) / perRow;
    const gridTop = rect.y + SPACING.lg + SPACING.lg + SPACING.xs; // below the heading band

    this.menuButtons.forEach(({ button }, i) => {
      const row = Math.floor(i / perRow);
      const col = i % perRow;
      const inRow = row === rows - 1 ? n - row * perRow : perRow;
      const rowW = inRow * cellW + (inRow - 1) * gap;
      const startX = rect.x + rect.width / 2 - rowW / 2 + cellW / 2;
      button.setWidth(cellW);
      button.setPosition(startX + col * (cellW + gap), gridTop + row * rowH + 22);
    });
    // Re-seat the keyboard focus ring on the (moved) highlighted button.
    if (this.menuOpen && this.menuNavIndex >= 0) this.setMenuNav(this.menuNavIndex);
  }

  /**
   * Seat the always-visible toggle for the current open/closed state. CLOSED: bottom-LEFT
   * of the walkable ground band (above the chat sheet), a quiet "≡" chip. OPEN: a small
   * tab hugging the drawer's top-left edge so the "×" close affordance reads as part of the
   * drawer and, being drawn ABOVE the scrim + panel, the tap to close always lands on it.
   * The toggle never moves with the slide tween (it stays put while the panel glides).
   */
  private positionToggle(): void {
    const toggle = this.menuToggle;
    if (!toggle) return;
    const g = this.groundRect;
    const rect = this.drawerRect;
    const tx = (this.menuOpen ? rect.x : g.x) + SPACING.md + toggle.buttonWidth / 2;
    // A tab just above the drawer when open — but never up into the top bar / greeting:
    // clamp it to stay within the ground band's top edge. Closed: bottom-left of the room.
    const ty = this.menuOpen
      ? Math.max(g.y + toggle.buttonHeight / 2, rect.y - SPACING.xs - toggle.buttonHeight / 2)
      : g.y + g.height - SPACING.sm - toggle.buttonHeight / 2;
    toggle.setPosition(tx, ty);
  }

  /**
   * Paint the cozy TAVERN INTERIOR programmatically (soft Graphics; swappable for a real
   * interior tileset later). A warm wooden-plank floor with rugs is the walkable area; a
   * plastered upper WALL band carries dusk windows, paintings, banners, shelves of
   * bottles, a notice-board and hanging herbs; a fuller glowing HEARTH and a bar counter
   * anchor the ends; the floor is dressed with more TABLES + chairs, BARRELS, CRATES,
   * potted PLANTS and CANDLES, all under hanging LANTERNS that pool amber light. Warm
   * amber/brown palette throughout — a lived-in snug inn where the residents gather, not a
   * field. DETAILED but still readable, never cluttered chaos: every prop is scenery only
   * (it never blocks walking — the whole rect stays a walkable tap target), so the room
   * stays open for the larger sprites to mill about.
   */
  private drawGround(): void {
    if (!this.ground) return;
    const g = this.groundRect;
    const gfx = this.ground;
    gfx.clear();

    const r = RADIUS.lg;
    // A thin plastered WALL band runs along the top; the plank FLOOR is the rest.
    const wallH = Math.min(64, Math.max(40, g.height * 0.18));
    const floorY = g.y + wallH;
    const floorH = g.height - wallH;
    const wide = g.width > 420;

    // ── plaster wall (warm clay) behind everything ──────────────────────────────
    gfx.fillStyle(TAVERN.wall, 1);
    gfx.fillRoundedRect(g.x, g.y, g.width, g.height, r);
    // A soft darker wainscot line where the wall meets the floor.
    gfx.fillStyle(TAVERN.wallShade, 0.6);
    gfx.fillRect(g.x, floorY - 4, g.width, 4);

    // ── wooden plank FLOOR (warm brown, with subtle plank seams + a few rugs) ─────
    gfx.fillStyle(TAVERN.floor, 1);
    // Bottom-rounded only: square top (it meets the wall), rounded bottom corners.
    gfx.fillRoundedRect(g.x, floorY, g.width, floorH, { tl: 0, tr: 0, bl: r, br: r });
    gfx.lineStyle(1, TAVERN.plankSeam, 0.5);
    const plankStep = 34;
    for (let py = floorY + plankStep; py < g.y + g.height - 4; py += plankStep) {
      gfx.lineBetween(g.x + 6, py, g.x + g.width - 6, py);
    }

    // A big central rug + a second smaller rug by the hearth so the floor reads layered.
    this.drawRug(gfx, g.x + g.width * 0.5, floorY + floorH * 0.6, Math.min(230, g.width * 0.42));
    this.drawRug(gfx, g.x + g.width * 0.18, floorY + floorH * 0.78, Math.min(150, g.width * 0.26));

    // ── the dusk WINDOW(s) on the wall ──────────────────────────────────────────
    const winW = Math.min(70, g.width * 0.14);
    const winH = Math.min(38, wallH - 14);
    this.drawWindow(gfx, g.x + g.width * 0.26, g.y + wallH / 2, winW, winH);
    if (g.width > 360) {
      this.drawWindow(gfx, g.x + g.width * 0.74, g.y + wallH / 2, winW, winH);
    }

    // ── wall dressing on the plaster band: a painting, a banner, a notice-board, a
    //    shelf of bottles, and a hanging herb bundle (only where there's room) ─────
    this.drawBanner(gfx, g.x + g.width * 0.5, g.y + 4, Math.min(44, wallH * 0.7));
    if (g.width > 340) {
      this.drawPainting(gfx, g.x + g.width * 0.4, g.y + wallH / 2, 34, Math.min(26, wallH - 18));
    }
    if (wide) {
      this.drawShelf(gfx, g.x + g.width * 0.6, g.y + wallH * 0.36, 64);
      this.drawNoticeBoard(
        gfx,
        g.x + g.width * 0.88,
        g.y + wallH / 2,
        40,
        Math.min(30, wallH - 16),
      );
      this.drawHerbs(gfx, g.x + g.width * 0.5, g.y + wallH);
    }

    // ── the glowing HEARTH / fireplace, set into the wall on the left (fuller now) ─
    const hearthW = Math.min(118, g.width * 0.24);
    this.drawHearth(
      gfx,
      g.x + Math.max(hearthW / 2 + 6, g.width * 0.13),
      g.y + wallH,
      hearthW,
      wallH,
    );

    // ── the BAR COUNTER along the right of the wall band, with bottles behind ─────
    if (g.width > 300) {
      const barW = Math.min(140, g.width * 0.26);
      this.drawBar(gfx, g.x + g.width - barW / 2 - 8, floorY - 2, barW);
    }

    // ── BARRELS + a CRATE tucked along the walls (scenery, never blocks walking) ──
    this.drawBarrel(gfx, g.x + 22, floorY + 14);
    this.drawBarrel(gfx, g.x + 22, floorY + 56);
    this.drawCrate(gfx, g.x + g.width - 26, floorY + floorH - 30);
    if (wide) this.drawBarrel(gfx, g.x + g.width - 26, floorY + floorH - 64);

    // ── more TABLES with chairs on the floor (scenery the crowd lingers by) ───────
    const tableR = 22;
    const tables: Array<{ x: number; y: number }> = [
      { x: g.x + g.width * 0.34, y: floorY + floorH * 0.32 },
      { x: g.x + g.width * 0.64, y: floorY + floorH * 0.46 },
      { x: g.x + g.width * 0.46, y: floorY + floorH * 0.72 },
    ];
    if (wide) tables.push({ x: g.x + g.width * 0.78, y: floorY + floorH * 0.74 });
    for (const t of tables) this.drawTable(gfx, t.x, t.y, tableR);

    // ── potted PLANTS in the corners + a standing CANDLE for cozy warmth ──────────
    this.drawPlant(gfx, g.x + g.width - 24, floorY + 16);
    if (wide) this.drawPlant(gfx, g.x + 26, floorY + floorH - 22);
    this.drawCandle(gfx, g.x + g.width * 0.5, floorY + floorH * 0.88);

    // ── hanging LANTERNS pooling warm light over the room ───────────────────────
    const lanternXs = g.width > 360 ? [0.34, 0.66] : [0.5];
    for (const fx of lanternXs) {
      this.drawLantern(gfx, g.x + g.width * fx, floorY + floorH * 0.4, floorH * 0.5);
    }

    // Tap the floor to walk the player there (touch-first movement). The whole interior
    // rect is the walkable hit area (furniture is scenery only — it never blocks a tap).
    gfx.setInteractive(
      new Phaser.Geom.Rectangle(g.x, g.y, g.width, g.height),
      Phaser.Geom.Rectangle.Contains,
    );
    gfx.off(Phaser.Input.Events.POINTER_DOWN);
    gfx.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (!this.player) return;
      // Same belt-and-suspenders depth-0 world-tap guard as the walker greet. Default
      // top-only hit-testing already gives a higher-depth control (the menu drawer/toggle,
      // "Set out", a chat row, a modal scrim) sitting over this ground graphics the tap;
      // re-check anyway so a tap meant for a control can never yank the player toward it —
      // only walk-to-tap when the ground itself is the GENUINE top-most hit under the pointer.
      if (!this.isTopHit(pointer, gfx)) return;
      this.player.targetX = Phaser.Math.Clamp(pointer.worldX, g.x + 28, g.x + g.width - 28);
      this.player.targetY = Phaser.Math.Clamp(pointer.worldY, g.y + 28, g.y + g.height - 28);
    });
  }

  /** A soft warm rug oval to break up the plank floor. */
  private drawRug(gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, w: number): void {
    const h = w * 0.46;
    gfx.fillStyle(TAVERN.rug, 0.85);
    gfx.fillEllipse(cx, cy, w, h);
    gfx.lineStyle(2, TAVERN.rugTrim, 0.8);
    gfx.strokeEllipse(cx, cy, w, h);
    gfx.strokeEllipse(cx, cy, w * 0.7, h * 0.7);
  }

  /** A dusk-blue window set into the plaster wall (a hint of night outside). */
  private drawWindow(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    w: number,
    h: number,
  ): void {
    // Frame.
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRoundedRect(cx - w / 2 - 3, cy - h / 2 - 3, w + 6, h + 6, RADIUS.sm);
    // Dusk sky inside (deep blue, a sliver of warmer horizon).
    gfx.fillStyle(TAVERN.dusk, 1);
    gfx.fillRect(cx - w / 2, cy - h / 2, w, h);
    gfx.fillStyle(TAVERN.duskGlow, 0.5);
    gfx.fillRect(cx - w / 2, cy + h / 2 - h * 0.3, w, h * 0.3);
    // Muntin bars (the classic window cross).
    gfx.lineStyle(2, TAVERN.beam, 1);
    gfx.lineBetween(cx, cy - h / 2, cx, cy + h / 2);
    gfx.lineBetween(cx - w / 2, cy, cx + w / 2, cy);
  }

  /**
   * The glowing hearth/fireplace — fuller + larger now: a stone surround with a mantel
   * shelf (a candle + a small pot on it), iron andirons, stacked ember logs, and three
   * tongues of fire over a wide glow pool that warms the floor in front. The cozy anchor
   * of the room.
   */
  private drawHearth(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    baseY: number,
    w: number,
    wallH: number,
  ): void {
    // Reach a touch ABOVE the wall band so the larger hearth reads as a proper fireplace.
    const h = wallH * 1.04;
    const top = baseY - h;
    // Warm glow pool spilling onto the floor in front of the fire (wider now).
    gfx.fillStyle(TAVERN.glow, 0.18);
    gfx.fillEllipse(cx, baseY + 18, w * 2.0, h * 1.2);
    gfx.fillStyle(TAVERN.glow, 0.1);
    gfx.fillEllipse(cx, baseY + 26, w * 2.6, h * 1.5);
    // Stone surround.
    gfx.fillStyle(TAVERN.stone, 1);
    gfx.fillRoundedRect(cx - w / 2, top, w, h, RADIUS.sm);
    // A mantel shelf jutting just above the firebox.
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRoundedRect(cx - w / 2 - 5, top - 6, w + 10, 8, RADIUS.sm);
    // A little candle + pot resting on the mantel.
    gfx.fillStyle(TAVERN.candle, 1);
    gfx.fillRect(cx - w * 0.28, top - 14, 4, 8);
    gfx.fillStyle(TAVERN.flameHi, 1);
    gfx.fillCircle(cx - w * 0.28 + 2, top - 15, 2);
    gfx.fillStyle(TAVERN.pot, 1);
    gfx.fillRoundedRect(cx + w * 0.16, top - 13, 12, 7, 2);
    // Dark firebox (arched feel via inner rounded rect).
    gfx.fillStyle(0x241712, 1);
    gfx.fillRoundedRect(cx - w / 2 + 9, top + 12, w - 18, h - 16, RADIUS.sm);
    // Iron andirons framing the fire.
    gfx.fillStyle(TAVERN.barrelHoop, 1);
    gfx.fillRect(cx - w * 0.3, baseY - 16, 3, 14);
    gfx.fillRect(cx + w * 0.3 - 3, baseY - 16, 3, 14);
    // Stacked ember logs.
    const fx = cx;
    const fbY = baseY - 6;
    gfx.fillStyle(TAVERN.emberLog, 1);
    gfx.fillRoundedRect(fx - w * 0.3, fbY - 6, w * 0.6, 8, RADIUS.sm);
    gfx.fillRoundedRect(fx - w * 0.22, fbY - 12, w * 0.44, 7, RADIUS.sm);
    // The fire: three tongues of flame for a fuller blaze.
    gfx.fillStyle(TAVERN.flameLo, 1);
    gfx.fillTriangle(fx - w * 0.26, fbY, fx + w * 0.26, fbY, fx, fbY - h * 0.62);
    gfx.fillStyle(TAVERN.flameLo, 0.9);
    gfx.fillTriangle(fx - w * 0.26, fbY, fx - w * 0.02, fbY, fx - w * 0.16, fbY - h * 0.4);
    gfx.fillTriangle(fx + w * 0.02, fbY, fx + w * 0.26, fbY, fx + w * 0.16, fbY - h * 0.4);
    gfx.fillStyle(TAVERN.flameHi, 1);
    gfx.fillTriangle(fx - w * 0.14, fbY, fx + w * 0.14, fbY, fx, fbY - h * 0.46);
  }

  /** The bar counter: a long wooden top with a darker base, lined with mugs + bottles. */
  private drawBar(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number, w: number): void {
    const h = 22;
    gfx.fillStyle(TAVERN.barBase, 1);
    gfx.fillRoundedRect(cx - w / 2, baseY - h, w, h, RADIUS.sm);
    gfx.fillStyle(TAVERN.barTop, 1);
    gfx.fillRoundedRect(cx - w / 2, baseY - h, w, 7, RADIUS.sm);
    // A row of bottles + a stacked plate standing ON the bar top.
    const bottleY = baseY - h - 1;
    const bottleCols = [-0.34, -0.18, 0.18, 0.34];
    bottleCols.forEach((f, i) => {
      gfx.fillStyle(i % 2 === 0 ? TAVERN.bottleGreen : TAVERN.bottleAmber, 1);
      gfx.fillRect(cx + w * f - 1.5, bottleY - 9, 3, 9);
      gfx.fillRect(cx + w * f - 0.5, bottleY - 13, 1, 4);
    });
    // A small pewter plate / pewter mug pair on the counter.
    gfx.fillStyle(TAVERN.pewter, 0.9);
    gfx.fillEllipse(cx, bottleY - 2, 9, 3);
    gfx.fillStyle(TAVERN.glow, 0.9);
    gfx.fillCircle(cx + w * 0.06, bottleY - 4, 3);
    // A couple of stools tucked under the bar.
    gfx.fillStyle(TAVERN.stool, 1);
    for (const sx of [-w * 0.3, 0, w * 0.3]) {
      gfx.fillCircle(cx + sx, baseY + 8, 7);
    }
  }

  /** A round table ringed with chairs — the social furniture residents linger by. */
  private drawTable(gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, rad: number): void {
    // Soft shadow.
    gfx.fillStyle(COLORS.scrim, 0.18);
    gfx.fillEllipse(cx, cy + rad * 0.5, rad * 2.2, rad * 0.72);
    // Four chairs around it (a seat + a little back-rest stub for a chair read).
    gfx.fillStyle(TAVERN.stool, 1);
    for (const a of [-Math.PI / 2, 0, Math.PI / 2, Math.PI]) {
      const sx = cx + Math.cos(a) * (rad + 13);
      const sy = cy + Math.sin(a) * (rad + 10);
      gfx.fillCircle(sx, sy, 8);
      gfx.fillRect(sx - 6, sy - 12, 12, 4); // back-rest hint
    }
    // Table top (warm wood with a lighter rim).
    gfx.fillStyle(TAVERN.tableBase, 1);
    gfx.fillCircle(cx, cy, rad);
    gfx.fillStyle(TAVERN.tableTop, 1);
    gfx.fillCircle(cx, cy, rad - 4);
    gfx.lineStyle(1.5, TAVERN.tableBase, 0.8);
    gfx.strokeCircle(cx, cy, rad - 4);
    // Cozy clutter on top: a plate, a mug, and a lit candle.
    gfx.fillStyle(TAVERN.pewter, 0.85);
    gfx.fillEllipse(cx - rad * 0.25, cy + rad * 0.12, 11, 5);
    gfx.fillStyle(TAVERN.barTop, 1);
    gfx.fillCircle(cx + rad * 0.32, cy - rad * 0.18, 4);
    gfx.fillStyle(TAVERN.candle, 1);
    gfx.fillRect(cx + rad * 0.02, cy - rad * 0.32, 3, 7);
    gfx.fillStyle(TAVERN.flameHi, 1);
    gfx.fillCircle(cx + rad * 0.02 + 1.5, cy - rad * 0.34, 2);
  }

  /** A hanging lantern on a cord, casting a soft amber light pool down the room. */
  private drawLantern(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    topY: number,
    poolH: number,
  ): void {
    // Cord up to the ceiling (the wall band above).
    gfx.lineStyle(1.5, TAVERN.beam, 0.9);
    gfx.lineBetween(cx, topY - 26, cx, topY);
    // Soft conical light pool beneath it (warm, low alpha so it never washes sprites out).
    gfx.fillStyle(TAVERN.glow, 0.12);
    gfx.fillTriangle(cx - 8, topY, cx + 8, topY, cx, topY + poolH * 0.5);
    gfx.fillEllipse(cx, topY + poolH * 0.5, poolH * 0.5, poolH * 0.22);
    // The lantern body: a warm glowing box with a dark cap.
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRoundedRect(cx - 8, topY - 26, 16, 6, 2);
    gfx.fillStyle(TAVERN.glow, 1);
    gfx.fillRoundedRect(cx - 7, topY - 20, 14, 16, RADIUS.sm);
    gfx.fillStyle(TAVERN.flameHi, 1);
    gfx.fillCircle(cx, topY - 12, 4);
  }

  /** A wooden ale barrel (feet at baseY) with iron hoops — scenery against a wall. */
  private drawBarrel(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number): void {
    const w = 26;
    const h = 32;
    const top = baseY - h;
    gfx.fillStyle(COLORS.scrim, 0.16);
    gfx.fillEllipse(cx, baseY + 2, w * 1.1, 6);
    gfx.fillStyle(TAVERN.barrel, 1);
    gfx.fillRoundedRect(cx - w / 2, top, w, h, 6);
    // Iron hoops.
    gfx.lineStyle(2, TAVERN.barrelHoop, 0.9);
    gfx.lineBetween(cx - w / 2, top + 7, cx + w / 2, top + 7);
    gfx.lineBetween(cx - w / 2, baseY - 7, cx + w / 2, baseY - 7);
    // Lid + a stave seam.
    gfx.fillStyle(TAVERN.barrelHoop, 0.7);
    gfx.fillEllipse(cx, top + 2, w - 4, 5);
    gfx.lineStyle(1, TAVERN.barrelHoop, 0.5);
    gfx.lineBetween(cx, top + 4, cx, baseY - 4);
  }

  /** A stacked wooden crate (feet at baseY) with slatted boards — scenery. */
  private drawCrate(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number): void {
    const s = 26;
    const top = baseY - s;
    gfx.fillStyle(COLORS.scrim, 0.16);
    gfx.fillEllipse(cx, baseY + 2, s * 1.1, 5);
    gfx.fillStyle(TAVERN.crate, 1);
    gfx.fillRoundedRect(cx - s / 2, top, s, s, 3);
    // Slats + frame.
    gfx.lineStyle(1.5, TAVERN.barrelHoop, 0.8);
    gfx.strokeRoundedRect(cx - s / 2, top, s, s, 3);
    gfx.lineBetween(cx - s / 2, top + s / 2, cx + s / 2, top + s / 2);
    gfx.lineBetween(cx, top, cx, top + s);
  }

  /** A wall shelf carrying a little row of bottles (mounted on the plaster band). */
  private drawShelf(gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, w: number): void {
    gfx.fillStyle(TAVERN.shelf, 1);
    gfx.fillRoundedRect(cx - w / 2, cy, w, 5, 2);
    // Bottles standing on it.
    const cols = [-0.34, -0.12, 0.12, 0.34];
    cols.forEach((f, i) => {
      gfx.fillStyle(i % 2 === 0 ? TAVERN.bottleGreen : TAVERN.bottleAmber, 1);
      const bx = cx + w * f;
      gfx.fillRect(bx - 2, cy - 12, 4, 12);
      gfx.fillRect(bx - 1, cy - 16, 2, 5);
    });
  }

  /** A small framed wall painting (a soft warm landscape) on the plaster band. */
  private drawPainting(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    w: number,
    h: number,
  ): void {
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRoundedRect(cx - w / 2 - 3, cy - h / 2 - 3, w + 6, h + 6, 2);
    gfx.fillStyle(TAVERN.paintingSky, 1);
    gfx.fillRect(cx - w / 2, cy - h / 2, w, h);
    // A hint of hills + a warm sun.
    gfx.fillStyle(TAVERN.leaf, 0.8);
    gfx.fillTriangle(cx - w / 2, cy + h / 2, cx, cy, cx + w / 2, cy + h / 2);
    gfx.fillStyle(TAVERN.glow, 0.9);
    gfx.fillCircle(cx + w * 0.28, cy - h * 0.22, Math.max(2, h * 0.12));
  }

  /** A hanging guild banner / pennant on the wall (a deep-red cloth with gold trim). */
  private drawBanner(gfx: Phaser.GameObjects.Graphics, cx: number, topY: number, h: number): void {
    const w = h * 0.62;
    gfx.fillStyle(TAVERN.bannerTrim, 1);
    gfx.fillRect(cx - w / 2 - 2, topY, w + 4, 4); // hanging rod
    gfx.fillStyle(TAVERN.banner, 1);
    // Cloth body with a notched (pennant) bottom.
    gfx.fillRect(cx - w / 2, topY + 4, w, h - 8);
    gfx.fillTriangle(cx - w / 2, topY + h - 4, cx + w / 2, topY + h - 4, cx, topY + h * 0.7);
    // A gold emblem dot + trim.
    gfx.lineStyle(1.5, TAVERN.bannerTrim, 0.9);
    gfx.strokeRect(cx - w / 2 + 2, topY + 6, w - 4, h - 12);
    gfx.fillStyle(TAVERN.bannerTrim, 1);
    gfx.fillCircle(cx, topY + h * 0.4, Math.max(2, h * 0.1));
  }

  /** A cork notice-board with a few pinned paper notes (the town's fixtures, in-fiction). */
  private drawNoticeBoard(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    w: number,
    h: number,
  ): void {
    gfx.fillStyle(TAVERN.beam, 1);
    gfx.fillRoundedRect(cx - w / 2 - 2, cy - h / 2 - 2, w + 4, h + 4, 2);
    gfx.fillStyle(TAVERN.board, 1);
    gfx.fillRect(cx - w / 2, cy - h / 2, w, h);
    // A few little pinned notes.
    gfx.fillStyle(TAVERN.note, 1);
    gfx.fillRect(cx - w * 0.32, cy - h * 0.22, w * 0.28, h * 0.34);
    gfx.fillRect(cx + w * 0.06, cy - h * 0.3, w * 0.26, h * 0.3);
    gfx.fillRect(cx + w * 0.02, cy + h * 0.06, w * 0.3, h * 0.3);
    // Faint ink lines on the notes.
    gfx.lineStyle(1, TAVERN.barrelHoop, 0.4);
    gfx.lineBetween(cx - w * 0.28, cy - h * 0.1, cx - w * 0.08, cy - h * 0.1);
    gfx.lineBetween(cx + w * 0.1, cy - h * 0.18, cx + w * 0.28, cy - h * 0.18);
  }

  /** A bundle of dried herbs hung from the wall band (cozy lived-in detail). */
  private drawHerbs(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number): void {
    gfx.lineStyle(1.5, TAVERN.beam, 0.9);
    gfx.lineBetween(cx, baseY - 22, cx, baseY - 14);
    gfx.fillStyle(TAVERN.herb, 1);
    // A fan of leafy sprigs tied at the top.
    for (const a of [-0.5, -0.2, 0.1, 0.4]) {
      gfx.fillTriangle(
        cx - 2,
        baseY - 14,
        cx + 2,
        baseY - 14,
        cx + Math.sin(a) * 12,
        baseY - 14 + 16,
      );
    }
    gfx.fillStyle(TAVERN.bannerTrim, 1);
    gfx.fillRect(cx - 3, baseY - 15, 6, 3); // the binding twine
  }

  /** A potted leafy plant standing on the floor (feet at baseY) — warm corner greenery. */
  private drawPlant(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number): void {
    gfx.fillStyle(COLORS.scrim, 0.16);
    gfx.fillEllipse(cx, baseY + 2, 22, 5);
    // Clay pot.
    gfx.fillStyle(TAVERN.pot, 1);
    gfx.fillRoundedRect(cx - 9, baseY - 12, 18, 12, 3);
    gfx.fillStyle(TAVERN.pot, 0.7);
    gfx.fillRect(cx - 10, baseY - 13, 20, 3);
    // Leaves fanning up.
    gfx.fillStyle(TAVERN.leaf, 1);
    for (const a of [-0.7, -0.25, 0.25, 0.7]) {
      gfx.fillTriangle(
        cx - 2,
        baseY - 12,
        cx + 2,
        baseY - 12,
        cx + Math.sin(a) * 16,
        baseY - 12 - 22 * Math.cos(a),
      );
    }
  }

  /** A standing wax candle on a small dish (feet at baseY), a warm flame on top. */
  private drawCandle(gfx: Phaser.GameObjects.Graphics, cx: number, baseY: number): void {
    gfx.fillStyle(COLORS.scrim, 0.14);
    gfx.fillEllipse(cx, baseY + 1, 14, 4);
    // Dish + a soft glow.
    gfx.fillStyle(TAVERN.glow, 0.14);
    gfx.fillEllipse(cx, baseY - 8, 28, 18);
    gfx.fillStyle(TAVERN.pewter, 0.9);
    gfx.fillEllipse(cx, baseY, 12, 4);
    // Wax body.
    gfx.fillStyle(TAVERN.candle, 1);
    gfx.fillRect(cx - 3, baseY - 16, 6, 16);
    // Flame.
    gfx.fillStyle(TAVERN.flameLo, 1);
    gfx.fillTriangle(cx - 3, baseY - 16, cx + 3, baseY - 16, cx, baseY - 24);
    gfx.fillStyle(TAVERN.flameHi, 1);
    gfx.fillCircle(cx, baseY - 18, 2);
  }

  private clampWalkersToGround(): void {
    const g = this.groundRect;
    // Keep the (now larger) sprites' feet comfortably inside the ground rect.
    const m = 28;
    for (const w of this.walkers) {
      const cx = Phaser.Math.Clamp(w.container.x, g.x + m, g.x + g.width - m);
      const cy = Phaser.Math.Clamp(w.container.y, g.y + m, g.y + g.height - m);
      w.container.setPosition(cx, cy);
      w.targetX = Phaser.Math.Clamp(w.targetX, g.x + m, g.x + g.width - m);
      w.targetY = Phaser.Math.Clamp(w.targetY, g.y + m, g.y + g.height - m);
      // Keep the wander state in sync with a relayout-driven reposition, else a resident
      // would snap back to its old (now-offscreen) goal on the next frame.
      if (w.wander) {
        w.wander.x = cx;
        w.wander.y = cy;
        w.wander.goalX = Phaser.Math.Clamp(w.wander.goalX, g.x + m, g.x + g.width - m);
        w.wander.goalY = Phaser.Math.Clamp(w.wander.goalY, g.y + m, g.y + g.height - m);
      }
    }
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so we must leave NOTHING
   * behind that a later re-entry would trip over: kill timers/tweens, remove the UPDATE
   * + keyboard listeners we added, and destroy every display object + walker we created.
   * (resetState() then re-empties the arrays at the next create().) Without this, the
   * buttons would stack with dead hit-areas and "Set out" would silently no-op.
   */
  private teardown(): void {
    // Per-frame + timers.
    this.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    this.chatTimer?.remove(false);
    this.chatTimer = undefined;
    this.tweens.killAll();
    this.time.removeAllEvents();

    // Keyboard: remove the keys we added so a re-entry rebinds cleanly (and Space/Enter
    // etc. from this scene never leak into the next). Also drop the drawer's Esc handler.
    this.input.keyboard?.off('keydown-ESC', this.onEscapeMenu, this);
    this.input.keyboard?.off('keydown-C', this.onCastKey, this);
    this.input.keyboard?.off('keydown-M', this.onMenuKey, this);
    this.input.keyboard?.removeAllKeys(true);
    this.cursors = undefined;
    this.wasd = undefined;
    this.dpad?.destroy();
    this.dpad = undefined;
    if (this.menuNavKeyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.menuNavKeyHandler);
    }
    this.menuNavKeyHandler = undefined;
    this.menuFocusRing?.destroy();
    this.menuFocusRing = undefined;

    // The hideable menu drawer (toggle + panel + heading + scrim + its live tween).
    this.menuTween?.remove();
    this.menuTween = undefined;
    this.menuToggle?.destroy();
    this.menuDrawer?.destroy();
    this.menuHeading?.destroy();
    this.menuScrim?.destroy();

    // Buttons (each removes its own scene-input listener on destroy via its own cleanup).
    for (const { button } of this.menuButtons) button.destroy();
    for (const b of this.quickReplies) b.destroy();
    this.setOutButton?.destroy();
    this.seamSign?.destroy();

    // Overhead bubbles (own their own dwell timers; destroy removes them cleanly).
    for (const { bubble } of this.bubbles) bubble.destroy();
    this.bubbles = [];

    // Walkers (containers own their child sprites; destroy cascades). Name tags now live
    // at SCENE level (not inside the container) so they must be destroyed explicitly.
    for (const w of this.walkers) {
      w.label?.destroy();
      w.questMarker?.destroy();
      w.container.destroy();
    }
    this.questGiverIds.clear();
    this.trails.clear();
    this.approachers.clear();

    // Chat + overlays.
    this.chatPanel?.destroy();
    this.chatList?.destroy();
    this.chatTitle?.destroy();
    this.chatInput?.destroy();
    this.greeting?.destroy();
    this.ground?.destroy();
    this.greetDialog?.destroy();
    this.greetMenu?.destroy();
    this.greetScrim?.destroy();

    // The tavern move-casting overlay (menu + scrim + the floating hint).
    this.castMenu?.destroy();
    this.castScrim?.destroy();
    this.castHint?.destroy();

    // Free-aim cast mode: tear down the aim layer/reticle/prompt/Done + unbind its keys.
    // (removeAllKeys(true) below also destroys the aim keys; this clears handlers + refs.)
    this.exitCastAimMode();

    // Drop references so a re-entry never touches a destroyed object before reset.
    this.walkers = [];
    this.player = undefined;
    this.follower = undefined;
    this.menuButtons = [];
    this.quickReplies = [];
    this.setOutButton = undefined;
    this.seamSign = undefined;
    this.seamTriggering = false;
    this.menuToggle = undefined;
    this.menuDrawer = undefined;
    this.menuHeading = undefined;
    this.menuScrim = undefined;
    this.menuTween = undefined;
    this.menuOpen = false;
    this.chatPanel = undefined;
    this.chatList = undefined;
    this.chatTitle = undefined;
    this.chatInput = undefined;
    this.greeting = undefined;
    this.ground = undefined;
    this.greetDialog = undefined;
    this.greetMenu = undefined;
    this.greetScrim = undefined;
    this.castMenu = undefined;
    this.castScrim = undefined;
    this.castHint = undefined;
    this.casting = false;
    this.castReactAt = null;
  }
}

/**
 * Reduce a walker's (possibly 8-way) facing Direction to the cardinal the cast point is
 * computed from. In Town movement is cardinal-only, but a diagonal value is mapped
 * defensively to its dominant axis so a cast always has a sensible "in front" direction.
 */
function toCardinalFacing(dir: Direction): 'north' | 'south' | 'east' | 'west' {
  switch (dir) {
    case 'north':
    case 'north-east':
    case 'north-west':
      return dir === 'north-east' ? 'east' : dir === 'north-west' ? 'west' : 'north';
    case 'south':
    case 'south-east':
    case 'south-west':
      return dir === 'south-east' ? 'east' : dir === 'south-west' ? 'west' : 'south';
    case 'east':
      return 'east';
    case 'west':
      return 'west';
    default:
      return 'south';
  }
}

/** A warm, in-character town greeting line (template floor; never toxic). */
function townGreetingLine(t: SimTrainer): string {
  const byStyle: Record<string, string> = {
    collector: 'Settling in? I just added a lovely new face to my dex — come see!',
    battler: 'Always up for a gentle spar if you fancy one — no stakes, just fun.',
    breeder: "I've a few spare egg-move darlings. Maybe a trade's in the cards?",
    explorer: 'Good to see a friendly face around town today.',
    socializer: 'There you are! The hearth is warmer with you here.',
  };
  return byStyle[t.playStyle] ?? 'Lovely to see you around town!';
}

/** Map a society beat-kind to a soft, human letter heading (mirrors TitleScene). */
function humanizeBeatKind(kind: string): string {
  switch (kind) {
    case 'evolution':
      return 'A new evolution';
    case 'catch':
      return 'A new catch';
    case 'levelup':
      return 'Growing stronger';
    case 'trade-offer':
    case 'trade-done':
      return 'A trade';
    case 'friendly-battle':
      return 'A friendly battle';
    case 'guild':
      return 'Your guild';
    case 'festival':
      return 'A festival in town';
    case 'lore-rumor':
      return 'A rumor';
    case 'player-praise':
    case 'greeting':
      return 'For you';
    default:
      return 'Word around town';
  }
}
